import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Table, Select, InputNumber, Input, Button, Spin, Tabs, Tag, message, Empty,
  Alert, Progress, Switch, Popconfirm, Tooltip, Popover, Modal, Checkbox, DatePicker,
} from 'antd';
import dayjs from 'dayjs';
import {
  PlusOutlined, DeleteOutlined, ReloadOutlined, DownloadOutlined,
  SaveOutlined, ArrowLeftOutlined, WarningFilled, CheckCircleFilled, InfoCircleOutlined, EditOutlined, SearchOutlined,
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { fetchProducts, fetchProductBom, fetchRequirement, fetchPlans, fetchPlanDetail, fetchPlanExport, createPlan, updatePlan, deletePlan } from './api';

/* ================================================================== *
 *  Tokens
 * ================================================================== */
const C = {
  ink: '#16233d', sub: '#64748b', mute: '#94a3b8', line: '#e2e8f0',
  bg: '#f1f5f9', white: '#fff',
  red: '#dc2626', redBg: '#fef2f2', green: '#059669', greenBg: '#ecfdf5',
  blue: '#2563eb', amber: '#b45309',
};

const S = {
  page: { padding: '14px 16px', backgroundColor: C.bg, minHeight: '100vh', fontFamily: "'DM Sans', system-ui, sans-serif" },
  card: { backgroundColor: C.white, borderRadius: 8, border: `1px solid ${C.line}`, boxShadow: '0 1px 2px rgba(15,23,42,.05)' },
  pad: { padding: '12px 14px' },
  h1: { fontSize: 17, fontWeight: 700, color: C.ink, margin: 0, letterSpacing: -0.2 },
  sub: { fontSize: 12.5, color: C.sub, marginTop: 1 },
  lbl: { fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: C.mute, display: 'block', marginBottom: 3 },
  mono: { fontFamily: "'JetBrains Mono', ui-monospace, monospace" },
  hint: { fontSize: 12, color: C.sub },
};

const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
const fmt = (v) => num(v).toLocaleString('en-IN', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) : '—');
/**
 * Which day (0 = the start date) batch i (0-based) runs on at batches_per_day,
 * which comes in halves: floor(i / per day), worked in whole halves. A half
 * carries over and the bigger day comes first - 1.5 a day runs 2, 1, 2, 1;
 * 0.5 a day runs a batch, then an off day. The server's endDateOf is the same rule.
 */
const dayOfBatch = (i, batchesPerDay) => Math.floor((2 * i) / Math.round(num(batchesPerDay) * 2));
/** How many calendar days n batches take at batches_per_day (10 at 1.5 = 7). */
const daysFor = (n, batchesPerDay) => dayOfBatch(num(n) - 1, batchesPerDay) + 1;

/**
 * Merge per-product rows into one procurement list.
 * Required ADDS UP across products; stock does NOT - it is the same physical
 * stock seen twice, so summing it would hide real shortages.
 *
 * Reserved on the combined row is what SAVED plans ahead in the queue took,
 * which is the reserved figure on the first product (in queue order) that
 * uses the material - later products' reserved also includes their siblings
 * in this same plan, which the combined row must not subtract twice.
 *
 * `sources` (optional, same order as `lists`) names each list's product -
 * { product_id, name, batches }. When given, every combined row carries a
 * `breakdown`: one line per product AND BOM line using that material (a
 * product that uses it at two stages gets two lines), in queue order, with
 * what it needs, what it gets from stock and what it is short. That is the
 * "who needs how much, and where" view behind a combined shortage.
 */
const mergeMaterials = (lists, sources) => {
  const by = new Map();
  lists.forEach((rows, li) => (rows || []).forEach((m) => {
    // material_id, not code - the backend allocates by id, so both must agree.
    const k = `id:${m.material_id}`;
    // Dispensed/planned exist only on a saved plan (see withDispensed); left
    // undefined elsewhere so those columns stay hidden.
    const hasDone = m.dispensed_qty !== undefined;
    if (!by.has(k)) by.set(k, { ...m, required_qty: 0, ...(hasDone ? { dispensed_qty: 0, planned_qty: 0 } : {}), repeat_lines: 0, _stdSet: new Set(), _stageSet: new Set(), _parts: [] });
    const t = by.get(k);
    t.required_qty = num(t.required_qty) + num(m.required_qty);
    if (hasDone) {
      t.dispensed_qty = num(t.dispensed_qty) + num(m.dispensed_qty);
      t.planned_qty = num(t.planned_qty) + num(m.planned_qty);
    }
    // The most BOM lines any one product has for this material - more than 1
    // means some product uses it at several stages.
    const lines = (m.lines || []).length ? m.lines : null;
    t.repeat_lines = Math.max(t.repeat_lines, lines ? lines.length : 1);
    if (sources && sources[li]) {
      const src = sources[li];
      // One breakdown line per BOM line. Batches belong to the product, not a
      // line, so they sit on its first line and span the rest (`span`).
      // Dispensed belongs to a STAGE (stageSlots) and spans that stage's lines.
      const own = lines || [{ stage: m.stage, bmr_standard_qty: m.bmr_standard_qty, required_qty: m.required_qty, short_qty: m.short_qty }];
      // What could not be put against a stage gets a line of its own, so the
      // product's lines still add up to its own Dispensed and Planned.
      const rest = hasDone && m.dispense_rest ? m.dispense_rest : null;
      const span = own.length + (rest ? 1 : 0);
      own.forEach((ln, i) => t._parts.push({
        product_id: src.product_id,
        product_name: src.name,
        batches: src.batches,
        span: i === 0 ? span : 0,
        multi_line: own.length > 1,
        ...(hasDone ? {
          planned_batches: src.batches + (src.done || 0),
          dispensed_batches: src.done || 0,
          dispensed_lines: i === 0 ? m.dispensed_lines || [] : [],
          planned_qty: ln.planned_qty !== undefined ? num(ln.planned_qty) : (i === 0 ? num(m.planned_qty) : 0),
          // Without a stage split (no lines) the whole product figure sits on its one line.
          dispense: ln.dispense || (i === 0 ? { span: own.length, qty: num(m.dispensed_qty), issued: m.dispensed_lines || [], missing: [] } : { span: 0 }),
          dispensed_qty: ln.dispense ? num(ln.dispense.qty) : (i === 0 ? num(m.dispensed_qty) : 0),
        } : {}),
        stage: ln.stage || null,
        dispensing_sequence: ln.dispensing_sequence ?? null,
        is_exact_duplicate: !!ln.is_exact_duplicate,
        bmr_standard_qty: num(ln.bmr_standard_qty),
        required_qty: num(ln.required_qty),
        // What this line actually gets = required - short (the backend
        // hands stock out in queue order, so earlier products get theirs
        // first, and inside a product the earlier stage first).
        allocated_qty: Math.max(0, num(ln.required_qty) - num(ln.short_qty)),
        short_qty: num(ln.short_qty),
      }));
      if (rest) {
        t._parts.push({
          product_id: src.product_id, product_name: src.name, batches: src.batches, span: 0, multi_line: true, is_rest: true,
          planned_batches: src.batches + (src.done || 0), dispensed_batches: src.done || 0, dispensed_lines: [],
          planned_qty: rest.planned, dispensed_qty: rest.qty, dispense: { span: 1, qty: rest.qty, issued: rest.issued, missing: [] },
          stage: null, dispensing_sequence: null, bmr_standard_qty: 0, required_qty: 0, allocated_qty: 0, short_qty: 0,
        });
      }
    }
    // Products can use the same material at different stages, so every
    // distinct stage is kept rather than whichever product came first - per
    // BOM line, since one product's row already lists several ("A, B").
    (lines ? lines.map((ln) => ln.stage) : [m.stage]).forEach((s) => { if (s) t._stageSet.add(s); });
    // Std/batch only means something if every product using this material
    // specifies the same figure; otherwise there is no single value to show.
    t._stdSet.add(num(m.bmr_standard_qty));
    t.has_invalid_std_qty = t.has_invalid_std_qty || m.has_invalid_std_qty;
    ['total_stock_qty', 'approved_qty', 'quarantine_qty', 'under_sampling_qty', 'under_test_qty', 'expired_qty', 'other_qty']
      .forEach((key) => { t[key] = num(m[key]); });
  }));
  // `lines` belongs to one product's row; a combined row has `breakdown`.
  return [...by.values()].map(({ _stdSet, _stageSet, _parts, lines, ...m }) => {
    const available = Math.max(0, num(m.approved_qty) - num(m.reserved_qty));
    return {
      ...m,
      ...(sources ? { breakdown: _parts } : {}),
      stage: _stageSet.size ? [..._stageSet].join(', ') : null,
      bmr_standard_qty: _stdSet.size === 1 ? [..._stdSet][0] : null,
      available_qty: available,
      short_qty: Math.max(0, num(m.required_qty) - available),
    };
  });
};

/**
 * What a saved plan has already dispensed, per product, from its Completed
 * rows. Dispensed is the ACTUAL quantity issued - each batch's stock card
 * issues added up across lots (`issued`, from the backend). The BOM standard
 * for the same batches (`stdQty`, from the frozen rows) is kept for Planned.
 *
 * The same issues are also kept per STAGE (`byStage`, from the backend's
 * issued_by_stage), so a material the BOM uses at two stages shows what each
 * stage was issued, batch by batch; `unmatched` is what could not be put
 * against a stage. `lineStd` is each BOM line's frozen std over all dispensed
 * batches (for Planned per line), `frozenLines` the lines a batch was frozen with.
 *
 * In Progress batches (`inProgressBatches`) are taken in too, for what they
 * have been issued so far - their material_details hold only the lines already
 * issued, which the live Required no longer counts - but they are not counted
 * in `batches`: a batch is dispensed once it has been issued everything.
 *   product_id -> { name, batches, batchList: [{ no, date }],
 *                   byMaterial: material_id -> { qty, stdQty, stage, std, lines: [{ no, date, qty }],
 *                                                byStage: stage -> { qty, issued: [{ no, date, qty }] },
 *                                                unmatched: { qty, issued }, lineStd: bom_line_id -> qty, frozenLines } }
 */
const dispensedOf = (completedBatches = [], inProgressBatches = []) => {
  const out = new Map();
  [...completedBatches.map((b) => ({ ...b, done: true })), ...inProgressBatches.map((b) => ({ ...b, done: false }))].forEach((b) => {
    if (!out.has(b.product_id)) out.set(b.product_id, { name: b.product_name, batches: 0, batchList: [], byMaterial: new Map() });
    const p = out.get(b.product_id);
    if (b.done) p.batches += 1;
    p.batchList.push({ no: b.product_batch_number, date: b.done ? b.completed_date : b.started_date });
    const issued = new Map((b.issued || []).map((i) => [Number(i.material_id), num(i.quantity_issued)]));
    (b.material_details || []).forEach((m) => {
      const cur = p.byMaterial.get(m.material_id) || {
        qty: 0, stdQty: 0, stage: m.stage || null, std: num(m.bmr_standard_qty), lines: [],
        byStage: new Map(), unmatched: { qty: 0, issued: [] }, lineStd: new Map(), frozenLines: m.lines || null,
      };
      const got = issued.get(Number(m.material_id)) || 0;
      const date = b.done ? b.completed_date : b.started_date;
      cur.qty += got;
      cur.stdQty += num(m.required_qty);
      cur.lines.push({ no: b.product_batch_number, date, qty: got });
      (m.lines || []).forEach((l) => cur.lineStd.set(Number(l.bom_line_id), num(cur.lineStd.get(Number(l.bom_line_id))) + num(l.required_qty)));
      const batch = { no: b.product_batch_number, date };
      if (Array.isArray(b.issued_by_stage)) {
        b.issued_by_stage.filter((e) => Number(e.material_id) === Number(m.material_id)).forEach((e) => {
          const qty = num(e.quantity_issued);
          if (e.matched) {
            const key = e.stage || '';
            if (!cur.byStage.has(key)) cur.byStage.set(key, { qty: 0, issued: [] });
            cur.byStage.get(key).qty += qty;
            cur.byStage.get(key).issued.push({ ...batch, qty });
          } else {
            cur.unmatched.qty += qty;
            cur.unmatched.issued.push({ ...batch, qty });
          }
        });
      } else if (got) {
        // An older backend sends no stage split: all of it is unmatched.
        cur.unmatched.qty += got;
        cur.unmatched.issued.push({ ...batch, qty: got });
      }
      p.byMaterial.set(m.material_id, cur);
    });
  });
  return out;
};

/**
 * A material's BOM lines, ready for the + breakdowns of a saved plan.
 * Every line gets planned_qty (std/batch x all planned batches: live required
 * + its frozen std over the dispensed batches). The FIRST line of each stage
 * gets `dispense` = { span, qty, issued: [{ no, date, qty }], missing: [{ no, date }] }
 * covering all lines of that stage (lines of one stage are adjacent in BMR
 * order, and the stock card does not split a stage further); the others get
 * { span: 0 }. `missing` = dispensed batches of the product that have not been
 * issued this stage yet. What could not be put against a stage - and any
 * Planned left over (a batch frozen with an older BOM) - is `dispense_rest`,
 * so every column still adds up to the material's own figure.
 */
const stageSlots = (lines, d, batchList, plannedTotal) => {
  const out = lines.map((l) => ({ ...l, planned_qty: num(l.required_qty) + num(d?.lineStd.get(Number(l.bom_line_id))) }));
  out.forEach((l, i) => {
    const key = l.stage || '';
    if (i > 0 && (out[i - 1].stage || '') === key) { l.dispense = { span: 0 }; return; }
    let span = 1;
    while (i + span < out.length && (out[i + span].stage || '') === key) span += 1;
    const slot = d?.byStage.get(key);
    const issued = (slot?.issued || []).filter((x) => x.qty !== 0);
    l.dispense = { span, qty: num(slot?.qty), issued, missing: batchList.filter((b) => !issued.some((x) => x.no === b.no)) };
  });
  const plannedRest = num(plannedTotal) - out.reduce((s, l) => s + l.planned_qty, 0);
  const rest = { qty: num(d?.unmatched.qty), issued: d?.unmatched.issued || [], planned: Math.abs(plannedRest) < 0.0005 ? 0 : plannedRest };
  return { lines: out, dispense_rest: rest.qty !== 0 || rest.planned !== 0 ? rest : null };
};

/**
 * Live rows of one product with dispensed (actual issued) and planned added.
 * Planned stays on the BOM standard (std × all batches), so it no longer
 * equals Dispensed + Required when the weighed quantity differs. Lines get
 * their own Planned and each stage its own Dispensed (stageSlots).
 */
const withDispensed = (rows, done) => (rows || []).map((m) => {
  const d = done?.byMaterial.get(m.material_id);
  const out = { ...m, dispensed_qty: num(d?.qty), planned_qty: num(m.required_qty) + num(d?.stdQty), dispensed_lines: d?.lines || [] };
  return (m.lines || []).length ? { ...out, ...stageSlots(m.lines, d, done?.batchList || [], out.planned_qty) } : out;
});

const sortShortFirst = (rows = []) => [...rows].sort(
  (a, b) => num(b.short_qty) - num(a.short_qty) || String(a.material_code || '').localeCompare(String(b.material_code || ''))
);

const exportMaterials = (rows, filename) => {
  if (!rows || rows.length === 0) { message.warning('Nothing to export'); return; }
  const sheet = XLSX.utils.json_to_sheet(sortShortFirst(rows).map((r) => ({
    'Material Code': r.material_code, 'Material Name': r.material_name, Stage: r.stage || '', UOM: r.uom,
    'Std/Batch': r.bmr_standard_qty === null ? '' : num(r.bmr_standard_qty),
    Required: num(r.required_qty),
    'Total Stock': num(r.total_stock_qty),
    Approved: num(r.approved_qty),
    'Reserved (earlier plans)': num(r.reserved_qty),
    Available: num(r.available_qty),
    Quarantine: num(r.quarantine_qty), 'Under Sampling': num(r.under_sampling_qty),
    'Under Test': num(r.under_test_qty), Expired: num(r.expired_qty),
    'Other (unrecognised QC status)': num(r.other_qty),
    Short: num(r.short_qty),
  })));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, sheet, 'Material Requirement');
  // Combined view: a second sheet with every material split by product, so
  // the order sheet also says which product each quantity is for.
  if (rows.some((r) => (r.breakdown || []).length)) {
    const lines = [];
    sortShortFirst(rows).forEach((r) => (r.breakdown || []).forEach((p) => lines.push({
      'Material Code': r.material_code, 'Material Name': r.material_name, UOM: r.uom,
      Product: p.product_name, Batches: p.batches, Stage: p.stage || '',
      'Std/Batch': p.bmr_standard_qty, Required: p.required_qty,
      'Gets from stock': p.allocated_qty, Short: p.short_qty,
    })));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(lines), 'By Product');
  }
  XLSX.writeFile(wb, `${filename}.xlsx`);
};

/* ================================================================== *
 *  Plan download - the planners' "Upto OCT RM" workbook layout
 *
 *  Same columns, order, header colours and widths as that sheet, minus
 *  the Buffer, Strength, Expired, Inventory (No. of Days), Appr/qty and QC
 *  columns (left out on request). Fixed numbers, no formulas; the server
 *  computes every figure with the same logic as the plan screen (see
 *  export_plan in the controller), plus BMR No. and Available.
 * ================================================================== */
const BLUE = 'FF9DC3E6';
const PLAN_EXPORT_COLS = [
  { h: 'Product', k: 'product', w: 34 },
  { h: 'BMR No.', k: 'bmr_no', w: 20 },
  { h: 'Batch size', k: 'batch_size', w: 18 },
  { h: 'Mat Code', k: 'mat_code', w: 13 },
  { h: 'Manufacturer', k: 'manufacturer', w: 18 },
  { h: 'Supplier', k: 'supplier', w: 18 },
  { h: 'Type', k: 'type', w: 7.3 },
  { h: 'Stage', k: 'stage', w: 12 },
  { h: 'Material Name', k: 'material_name', w: 44 },
  { h: 'UOM', k: 'uom', w: 7.6 },
  { h: 'Qty/ Batch', k: 'qty_per_batch', w: 11.4, fmt: '#,##0.000' },
  { h: 'NOB', k: 'nob', w: 9.3, fmt: '0' },
  { h: 'Qty Req for Product', k: 'qty_req_for_product', w: 13.6, fmt: '#,##0.000' },
  { h: 'Total Qty Req', k: 'total_qty_req', w: 15.3, fmt: '#,##0.000' },
  { h: 'Total Stock', k: 'total_stock', w: 16, fmt: '#,##0.000' },
  { h: 'Approved Qty', k: 'approved_qty', w: 16, fmt: '#,##0.000', fill: 'FF00B050' },
  // Same as the plan screen: approved stock left for this product after the
  // batches ahead of it in the queue. Shortage is measured on it.
  { h: 'Available', k: 'available', w: 14, fmt: '#,##0.000', fill: 'FF00B050' },
  { h: 'Under test', k: 'under_test', w: 13.4, fmt: '#,##0.000', fill: 'FFFFFF00' },
  { h: 'Quarantine', k: 'quarantine', w: 13.8, fmt: '#,##0.000', fill: 'FFC55A11' },
  { h: 'Under Sampling', k: 'under_sampling', w: 12.4, fmt: '#,##0.000', fill: 'FFB4C7E7' },
  { h: 'Shortage', k: 'shortage', w: 13.6, fmt: '#,##0.000', fill: 'FFFFE699' },
];

const downloadPlanWorkbook = async (data) => {
  // Loaded only when a download is asked for, so the page itself stays light.
  const ExcelJS = (await import('exceljs')).default;
  const wb = new ExcelJS.Workbook();
  wb.created = new Date();
  const ws = wb.addWorksheet('Material Plan', { views: [{ state: 'frozen', ySplit: 1 }] });
  ws.columns = PLAN_EXPORT_COLS.map((c) => ({ header: c.h, key: c.k, width: c.w }));

  const thin = { style: 'thin', color: { argb: 'FFBFBFBF' } };
  const header = ws.getRow(1);
  header.height = 29;
  PLAN_EXPORT_COLS.forEach((c, i) => {
    const cell = header.getCell(i + 1);
    cell.font = { bold: true, size: 9, name: 'Calibri' };
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    cell.border = { top: thin, left: thin, bottom: thin, right: thin };
    const fill = c.fill === undefined ? BLUE : c.fill;
    if (fill) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fill } };
  });

  (data.rows || []).forEach((r) => {
    const row = ws.addRow(r);
    row.font = { size: 9, name: 'Calibri' };
    PLAN_EXPORT_COLS.forEach((c, i) => {
      const cell = row.getCell(i + 1);
      if (c.fmt) cell.numFmt = c.fmt;
      cell.border = { top: thin, left: thin, bottom: thin, right: thin };
      cell.alignment = { vertical: 'middle', wrapText: c.k === 'material_name' || c.k === 'manufacturer' || c.k === 'supplier' };
    });
    if (num(r.shortage) > 0) row.getCell('shortage').font = { size: 9, name: 'Calibri', bold: true, color: { argb: 'FFC00000' } };
  });
  ws.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: PLAN_EXPORT_COLS.length } };

  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const d = new Date(data.generated_at || Date.now());
  const stamp = `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
  const safe = String(data.plan_name || data.plan_id || 'plan').replace(/[\\/:*?"<>|]+/g, ' ').trim();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${safe} - Material Plan ${stamp}.xlsx`;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 0);
};

/* ================================================================== *
 *  Material status download — every batch on its product's schedule,
 *  green when its materials are in stock, red when short
 * ================================================================== */

/**
 * Every batch of a saved plan (the plan detail data) laid out on its
 * product's schedule, with whether its materials are in stock.
 *
 * Dates: batch 1 on the start date, batches_per_day every calendar day after.
 * Dispensed batches take the first places, then In Progress, then the rest.
 * A product without a start date or batches/day keeps its batches, undated.
 *
 * Stock is the screen's own: each product's Available - approved stock left
 * after older plans and the products added before it - handed to its batches
 * in order, In Progress ones first for what they still wait for. A batch is
 * short when any ticked material cannot be covered in full; what it lacks is
 * listed, and one product's shortfalls add up to its Short on screen.
 *   -> [{ order, product_id, product, family, bmr, slot, total, per_day, lane,
 *         date, after_end, start_date, end_date,
 *         kind: 'done' | 'progress' | 'ok' | 'short' | 'nobom',
 *         batch_no, shorts: [{ code, qty, uom }], waiting: [code] }]
 */
const buildMaterialStatus = (d, bmrOf, familyOf = () => null) => {
  const live = new Map((d.products || []).map((p) => [p.product_id, p]));
  const byProduct = (list) => {
    const out = new Map();
    (list || []).forEach((b) => {
      if (!out.has(b.product_id)) out.set(b.product_id, []);
      out.get(b.product_id).push(b);
    });
    out.forEach((l) => l.sort((a, b) => num(a.batch_sequence) - num(b.batch_sequence)));
    return out;
  };
  const done = byProduct(d.completed_batches);
  const prog = byProduct(d.in_progress_batches);
  const rows = [];
  (d.plan_items || []).forEach((pi, order) => {
    const mats = live.get(pi.product_id)?.live_materials || [];
    const byId = new Map(mats.map((m) => [m.material_id, m]));
    const pool = new Map(mats.map((m) => [m.material_id, Math.max(0, num(m.available_qty))]));
    const lineOf = new Map(mats.flatMap((m) => (m.lines || []).map((l) => [Number(l.bom_line_id), { material_id: m.material_id, std: num(l.bmr_standard_qty) }])));
    // Hand stock to one batch; what it cannot get in full is its shortfall.
    const take = (needs) => {
      const shorts = [];
      needs.forEach((need, mid) => {
        if (!(need > 0)) return;
        const have = pool.get(mid) || 0;
        if (have + 1e-9 >= need) { pool.set(mid, have - need); return; }
        const m = byId.get(mid) || {};
        shorts.push({ code: m.material_code || m.material_name || `material ${mid}`, qty: need - have, uom: m.uom || '' });
        pool.set(mid, 0);
      });
      return shorts;
    };
    const slots = [
      ...(done.get(pi.product_id) || []).map((b) => ({ kind: 'done', batch_no: b.product_batch_number, shorts: [], waiting: [] })),
      ...(prog.get(pi.product_id) || []).map((b) => {
        const waiting = (b.lines || []).filter((l) => !l.issued);
        const needs = new Map();
        waiting.forEach((l) => {
          const ln = lineOf.get(Number(l.bom_line_id));
          if (ln) needs.set(ln.material_id, (needs.get(ln.material_id) || 0) + ln.std);
        });
        return { kind: 'progress', batch_no: b.product_batch_number, shorts: take(needs), waiting: [...new Set(waiting.map((l) => l.material_code || l.material_name))] };
      }),
    ];
    const fullBatch = new Map(mats.map((m) => [m.material_id, num(m.bmr_standard_qty)]));
    for (let k = 0; k < num(pi.running); k += 1) {
      const shorts = take(fullBatch);
      slots.push({ kind: !mats.length ? 'nobom' : shorts.length ? 'short' : 'ok', batch_no: null, shorts, waiting: [] });
    }
    const per = num(pi.batches_per_day);
    const start = pi.start_date && Math.round(per * 2) > 0 ? dayjs(pi.start_date) : null;
    const end = pi.end_date ? dayjs(pi.end_date) : null;
    slots.forEach((s, i) => {
      const date = start ? start.add(dayOfBatch(i, per), 'day') : null;
      rows.push({
        ...s, order, product_id: pi.product_id, product: pi.product_name, family: familyOf(pi.product_id) || '', bmr: bmrOf(pi.product_id) || pi.product_code || '',
        slot: i + 1, total: slots.length, per_day: start ? per : null,
        date, after_end: !!(date && end && date.isAfter(end, 'day')), start_date: pi.start_date, end_date: pi.end_date,
      });
    });
  });
  return rows;
};

const STATUS_STYLE = {
  ok: { text: 'Material available', fill: 'FFC6EFCE', font: 'FF006100' },
  short: { text: 'Material short', fill: 'FFFFC7CE', font: 'FF9C0006' },
  progress: { text: 'In progress', fill: 'FFFFEB9C', font: 'FF9C5700' },
  done: { text: 'Dispensed', fill: 'FFE7E6E6', font: 'FF595959' },
  nobom: { text: 'No BOM materials', fill: 'FFFFFFFF', font: 'FF7F7F7F' },
};

/**
 * The "Material status" workbook: one sheet, the Day plan - a block per date
 * listing each product running that day and its batch count, coloured by
 * whether the batches' materials are in stock.
 */
const downloadMaterialStatusWorkbook = async (detail, rows) => {
  const ExcelJS = (await import('exceljs')).default;
  const wb = new ExcelJS.Workbook();
  wb.created = new Date();
  const thin = { style: 'thin', color: { argb: 'FFBFBFBF' } };
  const box = { top: thin, left: thin, bottom: thin, right: thin };
  const solid = (argb) => ({ type: 'pattern', pattern: 'solid', fgColor: { argb } });
  const f = (extra = {}) => ({ size: 9, name: 'Calibri', ...extra });
  const heading = (ws) => {
    ws.getCell('A1').value = `${detail.plan_name || 'Untitled plan'} (${detail.plan_id}) — material status, ${dayjs().format('DD MMM YYYY HH:mm')}`;
    ws.getCell('A1').font = f({ size: 12, bold: true });
    ws.getCell('A2').value = 'Stock as on download: approved stock left after older plans, then products in the order added, then each product\'s batches by date. Batch 1 on the start date, then batches/day every calendar day (a half carries over: 1.5 a day = 2, 1, 2, 1 ...).';
    ws.getCell('A2').font = f({ italic: true, color: { argb: 'FF595959' } });
    // One line of coloured words, so it fits whatever widths the day blocks give the columns.
    ws.getCell('A3').value = {
      richText: [
        ...['ok', 'short', 'progress', 'done'].flatMap((k) => [
          { text: STATUS_STYLE[k].text, font: f({ bold: true, color: { argb: STATUS_STYLE[k].font } }) },
          { text: '   ', font: f() },
        ]),
      ],
    };
  };

  const dated = rows.filter((r) => r.date);

  // Day plan: a block per date (Description | Product | Batches/day |
  // No. of batches) side by side, each product running that day once with its
  // batches per day as planned (1.5, 2.5, 5) and that day's number of batches
  // (the same, or what is left on its last day), coloured: red if any of the
  // day's batches is short, else amber if any is in progress, else grey if all
  // are dispensed, else green. When only some are: "1.5 (1 short)". A red
  // cell's note says what is short and by how much over that day's batches.
  const dp = wb.addWorksheet('Day plan', { views: [{ state: 'frozen', ySplit: 6 }] });
  heading(dp);
  const dayKeys = [...new Set(dated.map((r) => r.date.format('YYYY-MM-DD')))].sort();
  if (!dayKeys.length) {
    dp.getCell('A5').value = 'No product of this plan has a start date and batches per day yet.';
  } else {
    const BLOCK = 5;                    // Description, Product, Batches/day, No. of batches, gap
    const NO = 3;                       // the No. of batches column inside a block
    dayKeys.forEach((key, i) => {
      const col = 1 + i * BLOCK;
      [12, 34, 11, 18, 2].forEach((w, k) => { dp.getColumn(col + k).width = w; });
      dp.mergeCells(5, col, 5, col + NO);
      const dh = dp.getRow(5).getCell(col);
      dh.value = dayjs(key).format('DD MMM YYYY · ddd');
      dh.font = f({ bold: true, size: 10 });
      dh.fill = solid('FF9DC3E6');
      dh.alignment = { horizontal: 'center' };
      ['Description', 'Product', 'Batches/day', 'No. of batches'].forEach((h, k) => {
        const c = dp.getRow(6).getCell(col + k);
        c.value = h;
        c.font = f({ bold: true });
        c.fill = solid('FFDDEBF7');
        c.border = box;
        c.alignment = { horizontal: 'center' };
      });
      const today = dated.filter((r) => r.date.format('YYYY-MM-DD') === key);
      [...new Set(today.map((r) => r.order))].sort((a, b) => a - b).forEach((order, k) => {
        const mine = today.filter((r) => r.order === order);
        const count = (kind) => mine.filter((r) => r.kind === kind).length;
        const kind = count('short') ? 'short' : count('progress') ? 'progress' : mine.every((r) => r.kind === 'done') ? 'done' : 'ok';
        const st = STATUS_STYLE[kind];
        // The day's number of batches as planned: batches/day (1.5, 2.5 ...),
        // and on the product's last day only what is left, so the days add up
        // to the total (10 at 1.5 = 1.5 x 6, then 1). Below 1 a day a whole
        // batch runs every other day, so a working day shows its real count.
        const rate = num(mine[0].per_day);
        const dayIdx = dayjs(key).diff(dayjs(mine[0].start_date), 'day');
        const planned = rate >= 1 ? Math.round(Math.min(rate, mine[0].total - rate * dayIdx) * 2) / 2 : mine.length;
        const some = (k) => (count(k) && count(k) < mine.length ? ` (${count(k)} ${k === 'short' ? 'short' : 'in progress'})` : '');
        const tag = kind === 'short' ? some('short') : kind === 'progress' ? some('progress') : '';
        const n = tag ? `${planned}${tag}` : planned;
        const shortBy = new Map();
        mine.forEach((r) => r.shorts.forEach((x) => {
          const t = shortBy.get(x.code) || { qty: 0, uom: x.uom };
          t.qty += x.qty;
          shortBy.set(x.code, t);
        }));
        const tip = [...shortBy].map(([code, t]) => `${code} short ${fmt(t.qty)} ${t.uom}`.trim());
        // Batches/day as a number, so 1.5 / 2.5 / 5 show as typed.
        [mine[0].family || '—', mine[0].product, mine[0].per_day, n].forEach((v, j) => {
          const c = dp.getRow(7 + k).getCell(col + j);
          c.value = v;
          c.fill = solid(st.fill);
          c.font = f({ color: { argb: st.font }, bold: j === NO });
          c.border = box;
          c.alignment = { vertical: 'middle', horizontal: j >= 2 ? 'center' : 'left', wrapText: j === 1 || j === NO };
        });
        if (tip.length) dp.getRow(7 + k).getCell(col + NO).note = tip.join('\n');
      });
    });
    const undated = [...new Set(rows.filter((r) => !r.date).map((r) => r.product))];
    if (undated.length) {
      const lastRow = 7 + Math.max(...dayKeys.map((key) => new Set(dated.filter((r) => r.date.format('YYYY-MM-DD') === key).map((r) => r.order)).size));
      const c = dp.getRow(lastRow + 1).getCell(1);
      c.value = `Not in the day plan (no start date or batches per day): ${undated.join(', ')}.`;
      c.font = f({ italic: true, color: { argb: 'FF595959' } });
    }
  }

  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const safe = String(detail.plan_name || detail.plan_id || 'plan').replace(/[\\/:*?"<>|]+/g, ' ').trim();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${safe} - Material Status ${dayjs().format('DD-MM-YYYY')}.xlsx`;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 0);
};

/* ================================================================== *
 *  Status strip — verdict and figures on ONE line, not five big tiles
 * ================================================================== */
const Stat = ({ label, value, color }) => {
  // Batch sizes are long strings ("1,01,880 Tablets (10.57 Kg)"), so the type
  // steps down rather than blowing the strip apart.
  const len = String(value ?? '').length;
  return (
    <div style={{ padding: '0 14px', borderLeft: `1px solid ${C.line}`, lineHeight: 1.15, maxWidth: 210 }}>
      <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: C.mute, whiteSpace: 'nowrap' }}>{label}</div>
      <div style={{ fontSize: len > 18 ? 11 : len > 10 ? 13 : 16, fontWeight: 700, color: color || C.ink, ...S.mono }}>{value}</div>
    </div>
  );
};

const StatusStrip = ({ materials, batches, products, batchesPossible, batchSize }) => {
  const short = (materials || []).filter((m) => num(m.short_qty) > 0);
  const ok = short.length === 0 && (materials || []).length > 0;
  return (
    <div style={{
      ...S.card, marginBottom: 10, padding: '9px 14px',
      backgroundColor: ok ? C.greenBg : (short.length ? C.redBg : C.white),
      borderColor: ok ? '#a7f3d0' : (short.length ? '#fecaca' : C.line),
      display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '1 1 320px', minWidth: 260 }}>
        {ok ? <CheckCircleFilled style={{ color: C.green, fontSize: 16 }} />
          : <WarningFilled style={{ color: short.length ? C.red : C.mute, fontSize: 16 }} />}
        <div style={{ fontSize: 13, fontWeight: 700, color: ok ? C.green : (short.length ? C.red : C.sub) }}>
          {ok ? `All materials covered — you can run ${batches} batch${batches === 1 ? '' : 'es'}`
            : short.length ? `${short.length} material${short.length === 1 ? '' : 's'} short for ${batches} batch${batches === 1 ? '' : 'es'}`
              : 'Nothing to calculate'}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
        <Stat label="Products" value={products} />
        <Stat label="Batches" value={batches} />
        {batchSize && <Stat label="Theoretical Batch Size" value={batchSize} />}
        <Stat label="Materials" value={(materials || []).length} />
        <Stat label="Short" value={short.length} color={short.length ? C.red : C.green} />
        <Stat label="Can Run" value={batchesPossible} color={C.green} />
      </div>
    </div>
  );
};

/**
 * The procurement answer, only when there is one to give. Biggest shortage
 * first; a long list shows the first SHORT_CHIPS and folds the rest behind a
 * toggle, so 49 chips do not push the table off the screen.
 */
const SHORT_CHIPS = 12;
const ShortList = ({ materials }) => {
  const [all, setAll] = useState(false);
  const short = (materials || []).filter((m) => num(m.short_qty) > 0).sort((a, b) => num(b.short_qty) - num(a.short_qty));
  if (!short.length) return null;
  const shown = all ? short : short.slice(0, SHORT_CHIPS);
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
      <span style={{ fontSize: 12, fontWeight: 700, color: C.red, alignSelf: 'center', marginRight: 2 }}>PROCURE ({short.length}):</span>
      {shown.map((m) => (
        <span key={m.material_id} style={{
          backgroundColor: C.white, border: '1px solid #fecaca', borderRadius: 5, padding: '2px 8px', fontSize: 12.5,
        }}>
          <span style={{ ...S.mono, fontWeight: 700, color: C.ink }}>{m.material_code || '—'}</span>
          <span style={{ color: C.red, fontWeight: 700, marginLeft: 6 }}>+{fmt(m.short_qty)}</span>
          <span style={{ color: C.mute, marginLeft: 3 }}>{m.uom || ''}</span>
        </span>
      ))}
      {short.length > SHORT_CHIPS && (
        <Button type="link" size="small" style={{ padding: '0 4px', fontSize: 12.5 }} onClick={() => setAll((v) => !v)}>
          {all ? 'Show fewer' : `+${short.length - SHORT_CHIPS} more`}
        </Button>
      )}
    </div>
  );
};

/**
 * Tab label for one product: the name is trimmed by width (the full name is
 * in the line under the tabs) and a badge says whether anything is short, so the planner can see
 * which product needs attention without opening every tab.
 */
const ProductTabLabel = ({ name, batches, done, materials }) => {
  const shortN = (materials || []).filter((m) => num(m.short_qty) > 0).length;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <span style={{ maxWidth: 190, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name || 'Product'}</span>
        {/* Saved plan: batches left and already dispensed. */}
        {done > 0
          ? <span style={{ ...S.mono, fontSize: 12, color: C.mute }}>{batches} left · <span style={{ color: C.blue }}>{done} dispensed</span></span>
          : <span style={{ ...S.mono, fontSize: 12, color: C.mute }}>× {batches}</span>}
        {materials && (shortN > 0
          ? <span style={{ fontSize: 11.5, fontWeight: 700, color: C.red, backgroundColor: C.redBg, border: '1px solid #fecaca', borderRadius: 10, padding: '0 6px', lineHeight: '16px' }}>{shortN} short</span>
          : <CheckCircleFilled style={{ color: '#6ee7b7', fontSize: 12, margin: 0 }} />)}
    </span>
  );
};

/**
 * A small (i) icon whose tooltip opens only when that icon itself is hovered,
 * never the header text around it. Clicking it does not sort the column.
 */
const Tip = ({ text }) => (
  <Tooltip title={text} styles={{ root: { maxWidth: 280 } }}>
    <InfoCircleOutlined onClick={(e) => e.stopPropagation()}
      style={{ color: C.mute, fontSize: 12, marginLeft: 4, cursor: 'help', verticalAlign: 'middle' }} />
  </Tooltip>
);
const Head = (title, tip) => <span style={{ whiteSpace: 'nowrap' }}>{title}<Tip text={tip} /></span>;

const Warnings = ({ items }) => {
  if (!items || items.length === 0) return null;
  return (
    <Alert type="warning" showIcon banner style={{ marginBottom: 10, fontSize: 12, padding: '6px 12px' }}
      message={<span style={{ fontSize: 12 }}>{items.join(' · ')}</span>} />
  );
};

/* ================================================================== *
 *  Material table — dense
 * ================================================================== */
/** Who took the reserved stock - plans ahead in the queue, oldest first. */
const ReservedCell = ({ value, by }) => {
  if (!num(value)) return <span style={{ ...S.mono, fontSize: 12.5, color: '#cbd5e1' }}>{fmt(0)}</span>;
  return (
    <Popover trigger="click" placement="left" title="Reserved by, in queue order" content={
      <div style={{ fontSize: 12, lineHeight: 1.6, maxWidth: 380 }}>
        {(by || []).map((b, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 14 }}>
            <span>
              <b>{b.plan_name || 'Untitled plan'}</b> · {b.product_name}
              {b.plan_created && <span style={{ color: C.mute }}> · created {fmtDate(b.plan_created)}</span>}
            </span>
            <span style={{ ...S.mono, fontWeight: 700 }}>{fmt(b.qty)}</span>
          </div>
        ))}
      </div>
    }>
      <span onClick={(e) => e.stopPropagation()} style={{ ...S.mono, fontSize: 12.5, fontWeight: 700, color: C.amber, borderBottom: '1px dotted #b45309', cursor: 'pointer' }}>{fmt(value)}</span>
    </Popover>
  );
};

const bdCell = (v, color, bold) => <span style={{ ...S.mono, fontSize: 12.5, color: num(v) === 0 ? '#cbd5e1' : color || '#334155', fontWeight: bold && num(v) ? 700 : 400 }}>{fmt(v)}</span>;
const bdShort = (v) => (num(v) > 0 ? <span style={{ ...S.mono, fontSize: 12, fontWeight: 700, color: C.red }}>{fmt(v)}</span> : <CheckCircleFilled style={{ color: '#6ee7b7', fontSize: 12 }} />);
const bdCount = (v, color) => <span style={{ ...S.mono, fontSize: 12.5, color: num(v) === 0 ? '#cbd5e1' : color }}>{v}</span>;

/**
 * Dispensed batch count. Hover it to see which batches were dispensed, and how
 * much of this material each was actually issued.
 */
const DispensedBatches = ({ value, lines }) => (num(value) === 0 ? bdCount(value) : (
  <Tooltip placement="right" title={
    <div style={{ fontSize: 12, lineHeight: 1.6, maxHeight: 320, overflowY: 'auto' }}>
      {(lines || []).map((b, i) => (
        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 14 }}>
          <span style={S.mono}>{b.no || '—'}</span>
          <span style={{ opacity: 0.75 }}>{fmtDate(b.date)}</span>
          <span style={{ ...S.mono, fontWeight: 700 }}>{fmt(b.qty)}</span>
        </div>
      ))}
    </div>
  }>
    <span style={{ borderBottom: `1px dotted ${C.blue}`, cursor: 'help' }}>{bdCount(value, C.blue)}</span>
  </Tooltip>
));

/**
 * Stage of one BOM line. When a product uses the material on several lines the
 * dispensing sequence is added - two lines can share a stage (e.g. Methylene
 * Chloride twice in Ethyl Cellulose Coating) and only the sequence tells them
 * apart. A line repeated with the same stage, sequence and quantity is flagged.
 */
const StageCell = ({ line, showSeq }) => (line.is_rest ? (
  <Tooltip title="Issued to a dispensed batch, but which stage it was for could not be worked out from Nucleus (no lot / part link, and its request does not name a stage). It is counted in the total, never guessed.">
    <span style={{ fontSize: 12.5, color: C.amber, fontStyle: 'italic', cursor: 'help' }}>Not matched to a stage</span>
  </Tooltip>
) : (
  <span style={{ fontSize: 12.5, color: C.sub }}>
    {line.stage || '—'}
    {showSeq && line.dispensing_sequence != null && <span style={{ color: C.mute }}> · seq {line.dispensing_sequence}</span>}
    {line.is_exact_duplicate && (
      <Tooltip title="This line appears more than once in the BOM with the same stage, sequence and quantity. Every line is counted, as the BOM says — please check the BOM.">
        <WarningFilled style={{ color: C.amber, fontSize: 12, marginLeft: 5 }} />
      </Tooltip>
    )}
  </span>
));

/**
 * What one STAGE of a material was issued over the product's dispensed
 * batches (see stageSlots), with "k/D": issued in k of its D dispensed
 * batches, amber when some have not had it yet. Hover lists the batches and
 * those still without it - the stage is still to come, or the alternate code
 * was used. `rest` = the line for what could not be matched to a stage.
 */
const StageDispensed = ({ d, rest }) => {
  if (!d) return bdCell(0);
  const issued = d.issued || [];
  const missing = d.missing || [];
  const all = issued.length + missing.length;
  if (!all) return bdCell(d.qty, C.blue, true);
  const row = (b, i, qty) => (
    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 14 }}>
      <span style={S.mono}>{b.no || '—'}</span>
      <span style={{ opacity: 0.75 }}>{fmtDate(b.date)}</span>
      {qty !== undefined && <span style={{ ...S.mono, fontWeight: 700 }}>{fmt(qty)}</span>}
    </div>
  );
  return (
    <Tooltip placement="left" styles={{ root: { maxWidth: 380 } }} title={
      <div style={{ fontSize: 12, lineHeight: 1.6, maxHeight: 320, overflowY: 'auto' }}>
        {rest && <div style={{ marginBottom: 4 }}>Issued to these batches; the stage could not be worked out:</div>}
        {issued.length ? issued.map((b, i) => row(b, i, b.qty)) : <div>Not issued in any dispensed batch yet.</div>}
        {missing.length > 0 && <>
          <div style={{ marginTop: 6, fontWeight: 700 }}>Not issued yet in {missing.length} dispensed batch{missing.length === 1 ? '' : 'es'}:</div>
          {missing.map((b, i) => row(b, `m${i}`))}
          <div style={{ opacity: 0.75, marginTop: 2 }}>The stage is still to come, or the alternate code was used.</div>
        </>}
      </div>
    }>
      <span style={{ cursor: 'help', whiteSpace: 'nowrap' }}>
        {bdCell(d.qty, C.blue, true)}
        {!rest && (
          <span style={{
            marginLeft: 5, fontSize: 11, padding: '0 4px', borderRadius: 4, ...S.mono,
            border: `1px solid ${missing.length ? '#fcd34d' : C.line}`, color: missing.length ? C.amber : C.sub,
            backgroundColor: missing.length ? '#fffbeb' : '#f8fafc',
          }}>{issued.length}/{all}</span>
        )}
      </span>
    </Tooltip>
  );
};

/**
 * The expanded view of one material in a PRODUCT tab, when the BOM uses it on
 * more than one line: every stage it is used in, how much each line needs, and
 * how the product's share of stock is split between them - the earlier line
 * in the BMR gets stock first. Totals match the material's row.
 *
 * Saved plan: also Planned per line and Dispensed per STAGE (spanning the
 * stage's lines) with the batches each stage was issued to, plus a line for
 * whatever could not be matched to a stage - so Planned and Dispensed add up
 * to the material's own figures too.
 */
const LineBreakdown = ({ row }) => {
  const lines = row.lines || [];
  const saved = row.dispensed_qty !== undefined && lines.some((l) => l.dispense);
  const rest = saved && row.dispense_rest ? row.dispense_rest : null;
  const data = [
    ...lines.map((l, i) => ({ ...l, key: l.bom_line_id ?? i, n: i + 1 })),
    ...(rest ? [{
      key: 'rest', is_rest: true, bmr_standard_qty: null, planned_qty: rest.planned, required_qty: 0, short_qty: 0,
      dispense: { span: 1, qty: rest.qty, issued: rest.issued, missing: [] },
    }] : []),
  ];
  const tot = data.reduce((s, l) => ({
    std: s.std + num(l.bmr_standard_qty), plan: s.plan + num(l.planned_qty),
    done: s.done + (l.dispense && l.dispense.span ? num(l.dispense.qty) : 0), req: s.req + num(l.required_qty),
    got: s.got + Math.max(0, num(l.required_qty) - num(l.short_qty)), short: s.short + num(l.short_qty),
  }), { std: 0, plan: 0, done: 0, req: 0, got: 0, short: 0 });
  const cols = [
    { title: '#', key: 'n', width: 34, align: 'center', render: (_, l) => <span style={{ fontSize: 12, color: C.mute }}>{l.n || ''}</span> },
    { title: 'Stage', key: 'stage', render: (_, l) => <StageCell line={l} showSeq /> },
    { title: 'Std/Batch', dataIndex: 'bmr_standard_qty', key: 'std', width: 100, align: 'right', render: (v) => (v === null ? '' : bdCell(v)) },
    ...(saved ? [
      { title: Head('Planned', 'Std/batch × all planned batches, per line.'), dataIndex: 'planned_qty', key: 'plan', width: 110, align: 'right', render: (v) => bdCell(v) },
      {
        // Per stage: the stock card does not split one stage's lines further.
        title: Head('Dispensed', 'Actually issued for this stage, over the dispensed batches - k/D = issued in k of D dispensed batches. Two lines of one stage share one figure. Hover for the batches.'),
        key: 'done', width: 150, align: 'right',
        onCell: (l) => ({ rowSpan: l.dispense ? l.dispense.span : 1 }),
        render: (_, l) => <StageDispensed d={l.dispense} rest={l.is_rest} />,
      },
    ] : []),
    { title: saved ? Head('Required', 'Still needed: std/batch × batches left.') : 'Required', dataIndex: 'required_qty', key: 'req', width: 110, align: 'right', render: (v) => bdCell(v, C.ink, true) },
    { title: Head('Gets from stock', 'How this product\'s share of approved stock is split between the stages: the earlier line in the BMR gets it first.'), key: 'got', width: 120, align: 'right', render: (_, l) => bdCell(Math.max(0, num(l.required_qty) - num(l.short_qty)), C.green, true) },
    { title: 'Short', dataIndex: 'short_qty', key: 'short', width: 110, align: 'right', render: (v, l) => (l.is_rest ? '' : bdShort(v)) },
  ];
  return (
    <div style={{ padding: '4px 4px 6px 28px', maxWidth: saved ? 1000 : 760 }}>
      <div style={{ fontSize: 12.5, color: C.sub, marginBottom: 6 }}>
        <b style={{ color: C.ink }}>{row.material_code}</b> {row.material_name} is used <b>{lines.length} times</b> in this BOM
        {' · '}<b style={S.mono}>{fmt(tot.std)}</b> {row.uom || ''} per batch in all · stock is handed out top to bottom.
      </div>
      <Table size="small" bordered pagination={false} columns={cols} dataSource={data}
        rowClassName={(l) => (num(l.short_qty) > 0 ? 'mm-short' : '')}
        summary={() => (
          <Table.Summary.Row>
            <Table.Summary.Cell index={0} colSpan={2}><span style={{ fontSize: 12.5, fontWeight: 700 }}>Total</span></Table.Summary.Cell>
            <Table.Summary.Cell index={1} align="right">{bdCell(tot.std)}</Table.Summary.Cell>
            {saved && <>
              <Table.Summary.Cell index={2} align="right">{bdCell(tot.plan)}</Table.Summary.Cell>
              <Table.Summary.Cell index={3} align="right">{bdCell(tot.done, C.blue, true)}</Table.Summary.Cell>
            </>}
            <Table.Summary.Cell index={4} align="right">{bdCell(tot.req, C.ink, true)}</Table.Summary.Cell>
            <Table.Summary.Cell index={5} align="right">{bdCell(tot.got, C.green, true)}</Table.Summary.Cell>
            <Table.Summary.Cell index={6} align="right">{tot.short > 0 ? <span style={{ ...S.mono, fontSize: 12, fontWeight: 700, color: C.red }}>{fmt(tot.short)}</span> : bdCell(0)}</Table.Summary.Cell>
          </Table.Summary.Row>
        )} />
    </div>
  );
};

/**
 * The expanded view of one combined material: which product needs how much of
 * it, at which stage, how much each gets from stock and how much each is short
 * - in queue order, which is the order stock is handed out. A product that uses
 * the material at several stages has one line per stage; its product, batch
 * and dispensed cells span those lines. Totals match the combined row.
 */
const MaterialBreakdown = ({ row }) => {
  const parts = row.breakdown || [];
  // Saved plan: also what is planned and already dispensed, per product.
  const hasDone = parts.some((p) => p.dispensed_qty !== undefined);
  const tot = parts.reduce((s, p) => ({
    plan: s.plan + num(p.planned_qty), done: s.done + num(p.dispensed_qty),
    req: s.req + p.required_qty, got: s.got + p.allocated_qty, short: s.short + p.short_qty,
  }), { plan: 0, done: 0, req: 0, got: 0, short: 0 });
  const productCount = new Set(parts.map((p) => p.product_id)).size;
  const lineCount = parts.filter((p) => !p.is_rest).length;
  const cell = bdCell;
  const count = bdCount;
  // Product-level cells cover all of that product's lines.
  const spanned = (col) => ({ ...col, onCell: (p) => ({ rowSpan: p.span ?? 1 }) });
  const cols = [
    spanned({ title: '#', key: 'n', width: 34, align: 'center', render: (_, p) => <span style={{ fontSize: 12, color: C.mute }}>{p.product_no}</span> }),
    // A fixed width: in the scroll area a column without one collapses to
    // nothing when the screen is narrow or zoomed in, and the product name,
    // one letter per line, made each row too tall to show any figures.
    spanned({ title: 'Product', dataIndex: 'product_name', key: 'product_name', width: 220, render: (v) => <span style={{ fontSize: 12, color: C.ink }}>{v}</span> }),
    hasDone ? {
      title: 'Batches', key: 'batches',
      children: [
        spanned({ title: 'Planned', dataIndex: 'planned_batches', key: 'pb', width: 66, align: 'center', render: (v) => count(v) }),
        spanned({
          // Hover the count to see which batches were dispensed, and how much
          // of this material each was actually issued.
          title: 'Dispensed', dataIndex: 'dispensed_batches', key: 'db', width: 76, align: 'center',
          render: (v, p) => <DispensedBatches value={v} lines={p.dispensed_lines} />,
        }),
        spanned({ title: 'Left', dataIndex: 'batches', key: 'batches', width: 52, align: 'center', render: (v) => count(v) }),
      ],
    } : spanned({ title: 'Batches', dataIndex: 'batches', key: 'batches', width: 70, align: 'center', render: (v) => <span style={{ ...S.mono, fontSize: 12.5 }}>{v}</span> }),
    { title: 'Stage', dataIndex: 'stage', key: 'stage', width: 230, render: (_, p) => <StageCell line={p} showSeq={p.multi_line} /> },
    { title: 'Std/Batch', dataIndex: 'bmr_standard_qty', key: 'std', width: 100, align: 'right', render: (v, p) => (p.is_rest ? '' : cell(v)) },
    ...(hasDone ? [
      { title: Head('Planned', 'Std/batch × all planned batches.'), dataIndex: 'planned_qty', key: 'plan', width: 110, align: 'right', render: (v) => cell(v) },
      // Per STAGE of the product: two lines of one stage share one figure.
      {
        title: Head('Dispensed', 'Actually issued for this stage, over the product\'s dispensed batches: the stock card issues of every lot, added up. k/D = issued in k of D dispensed batches. Hover for the batches.'),
        key: 'done', width: 150, align: 'right',
        onCell: (p) => ({ rowSpan: p.dispense ? p.dispense.span : 1 }),
        render: (_, p) => <StageDispensed d={p.dispense} rest={p.is_rest} />,
      },
    ] : []),
    { title: hasDone ? Head('Required', 'Still needed: std/batch × batches left.') : 'Required', dataIndex: 'required_qty', key: 'req', width: 110, align: 'right', render: (v) => cell(v, C.ink, true) },
    { title: Head('Gets from stock', 'How much approved stock this line gets. Older plans get stock first, then products in the order they were added, and inside a product the earlier stage.'), dataIndex: 'allocated_qty', key: 'got', width: 120, align: 'right', render: (v) => cell(v, C.green, true) },
    { title: 'Short', dataIndex: 'short_qty', key: 'short', width: 110, align: 'right', render: (v, p) => (p.is_rest ? '' : bdShort(v)) },
  ];
  let productNo = 0;
  return (
    <div style={{ padding: '4px 4px 6px 28px' }}>
      <div style={{ fontSize: 12.5, color: C.sub, marginBottom: 6 }}>
        <b style={{ color: C.ink }}>{row.material_code}</b> {row.material_name} is used by <b>{productCount}</b> product{productCount === 1 ? '' : 's'} in this plan
        {lineCount > productCount && <> on <b>{lineCount}</b> BOM lines</>}
        {num(row.reserved_qty) > 0 && <> · <span style={{ color: C.amber }}>{fmt(row.reserved_qty)} {row.uom || ''} of the approved stock is already reserved by earlier plans</span></>}
        {' · '}stock is handed out top to bottom.
      </div>
      {/* A short scroll area of its own, header and Total fixed inside it, so
          the opened material row above and the whole breakdown fit on screen
          together however many products use the material. */}
      <Table size="small" bordered pagination={false} columns={cols} scroll={{ y: 300 }}
        dataSource={parts.map((p, i) => ({ ...p, product_no: (p.span ?? 1) > 0 ? ++productNo : productNo, key: `${p.product_id}-${i}` }))}
        rowClassName={(p) => (p.short_qty > 0 ? 'mm-short' : '')}
        summary={() => (
          <Table.Summary fixed>
          <Table.Summary.Row>
            <Table.Summary.Cell index={0} colSpan={hasDone ? 7 : 5}><span style={{ fontSize: 12.5, fontWeight: 700 }}>Total</span></Table.Summary.Cell>
            {hasDone && <>
              <Table.Summary.Cell index={1} align="right">{cell(tot.plan)}</Table.Summary.Cell>
              <Table.Summary.Cell index={2} align="right">{cell(tot.done, C.blue, true)}</Table.Summary.Cell>
            </>}
            <Table.Summary.Cell index={3} align="right">{cell(tot.req, C.ink, true)}</Table.Summary.Cell>
            <Table.Summary.Cell index={4} align="right">{cell(tot.got, C.green, true)}</Table.Summary.Cell>
            <Table.Summary.Cell index={5} align="right">{tot.short > 0 ? <span style={{ ...S.mono, fontSize: 12, fontWeight: 700, color: C.red }}>{fmt(tot.short)}</span> : cell(0)}</Table.Summary.Cell>
          </Table.Summary.Row>
          </Table.Summary>
        )} />
    </div>
  );
};

/**
 * `completed` shows what a dispensed batch needed and nothing else: stock
 * figures mean nothing for a batch that has already run, and older frozen
 * rows carry a stock snapshot taken after their own dispense.
 *
 * `batches` (one product): { planned, dispensed, left } for that product - the
 * same Batches columns the combined breakdown shows per product. A new plan
 * passes only `planned`; Dispensed and Left are shown when given. Avail. for
 * (enoughFor) is always added to the group.
 */
/**
 * How many more batches the stock covers: Available (approved stock left for
 * this product after the plans ahead of it in the queue) ÷ what ONE batch
 * needs, rounded down - per material the same division "Can Run" takes the
 * lowest of. null when the BOM gives no amount per batch. The small epsilon
 * keeps 822.756 ÷ 411.378 from landing on 1.9999999.
 */
const enoughFor = (m) => (num(m.bmr_standard_qty) > 0
  ? Math.max(0, Math.floor(num(m.available_qty) / num(m.bmr_standard_qty) + 1e-9))
  : null);

const MaterialTable = ({ rows, loading, emptyText, completed = false, batches }) => {
  const [shortOnly, setShortOnly] = useState(false);
  // Kept here: a fixed pageSize in the pagination config overrides whatever
  // is picked in the 20 / 40 / 100 dropdown, so the dropdown did nothing.
  const [pageSize, setPageSize] = useState(40);
  const data = useMemo(() => {
    const base = completed ? [...(rows || [])] : sortShortFirst(rows || []);
    return (shortOnly && !completed ? base.filter((m) => num(m.short_qty) > 0) : base).map((r, i) => ({ ...r, key: r.material_id ?? i }));
  }, [rows, shortOnly, completed]);
  const shortCount = completed ? 0 : (rows || []).filter((m) => num(m.short_qty) > 0).length;
  // Only when something ahead in the queue actually took stock - a lone plan
  // looks exactly as it did before.
  const showReserved = !completed && (rows || []).some((m) => num(m.reserved_qty) > 0);
  // Saved plan only: what is planned in all and already dispensed.
  const showDone = !completed && (rows || []).some((m) => m.dispensed_qty !== undefined);
  // Avail. for, capped at the batches still to run.
  const availFor = (m) => {
    const n = enoughFor(m);
    const left = batches ? (batches.left ?? batches.planned) : undefined;
    return n === null || left === undefined ? n : Math.min(n, num(left));
  };

  // A real 0.000 is printed rather than a dash, so an empty bucket cannot be
  // mistaken for data that failed to load. Only a genuinely absent value
  // (null/undefined) shows a dash.
  const q = (title, key, color, bold, tip) => ({
    title: tip ? Head(title, tip) : title,
    dataIndex: key, key, width: 96, align: 'right',
    sorter: (a, b) => num(a[key]) - num(b[key]),
    render: (v) => {
      if (v === null || v === undefined) return <span style={{ color: C.mute }}>—</span>;
      const z = num(v) === 0;
      return <span style={{ ...S.mono, fontSize: 12.5, color: z ? '#cbd5e1' : (color || '#334155'), fontWeight: bold && !z ? 700 : 400 }}>{fmt(v)}</span>;
    },
  });

  const allColumns = [
    {
      title: 'Code', dataIndex: 'material_code', key: 'material_code', width: 150, fixed: 'left',
      sorter: (a, b) => String(a.material_code || '').localeCompare(String(b.material_code || '')),
      render: (v, r) => {
        // ×N: the BOM uses this material on N lines (several stages, or one
        // stage twice). Product row: its own lines; combined row: the most
        // any one product has.
        const n = r.breakdown ? num(r.repeat_lines) : (r.lines || []).length;
        return (
          <span style={{ whiteSpace: 'nowrap' }}>
            <span style={{ ...S.mono, fontSize: 12.5, fontWeight: 600, color: C.ink }}>{v || '—'}</span>
            {n > 1 && (
              <Tooltip styles={{ root: { maxWidth: 340 } }} title={r.breakdown
                ? 'Used on more than one line of a product\'s BOM (different stages or quantities). Click the row to see each product and stage.'
                : <div style={{ fontSize: 12, lineHeight: 1.6 }}>
                    Used {n} times in this BOM:
                    {r.lines.map((l, i) => <div key={i}>• {l.stage || '—'}{l.dispensing_sequence != null ? ` · seq ${l.dispensing_sequence}` : ''} — <b>{fmt(l.bmr_standard_qty)}</b> {r.uom || ''}</div>)}
                    = <b>{fmt(r.bmr_standard_qty)}</b> {r.uom || ''} per batch. Click + to see each stage.
                  </div>}>
                <Tag color="blue" style={{ marginInlineEnd: 0, marginLeft: 5, fontSize: 11, lineHeight: '16px', padding: '0 4px', cursor: 'help' }}>×{n}</Tag>
              </Tooltip>
            )}
          </span>
        );
      },
    },
    {
      // A fixed width: with sideways scrolling an ellipsis column without one
      // collapses to nothing, which hid material names entirely.
      title: 'Material Name', dataIndex: 'material_name', key: 'material_name', width: 280, ellipsis: true,
      render: (v, r) => (
        <span style={{ fontSize: 12 }}>
          {v || '—'}
          {r.has_invalid_std_qty && (
            <Tooltip title="The amount per batch for this material is missing in the BOM, so it is counted as 0. Please check the BOM.">
              <WarningFilled style={{ color: C.amber, fontSize: 12, marginLeft: 5 }} />
            </Tooltip>
          )}
        </span>
      ),
    },
    // The product's batches, the same on every row; Dispensed hovers to show
    // what each dispensed batch was issued of this material.
    ...(batches && !completed ? [{
      title: 'Batches', key: 'batches',
      children: [
        { title: 'Planned', key: 'pb', width: 66, align: 'center', render: () => bdCount(batches.planned) },
        ...(batches.dispensed !== undefined ? [{ title: 'Dispensed', key: 'db', width: 76, align: 'center', render: (_, r) => <DispensedBatches value={batches.dispensed} lines={r.dispensed_lines} /> }] : []),
        ...(batches.left !== undefined ? [{ title: 'Left', key: 'lb', width: 52, align: 'center', render: () => bdCount(batches.left) }] : []),
        {
          // Never more than the batches left (the planner's choice: stock for
          // 40 against 10 left reads 10). Red exactly when the material is
          // short, so it never disagrees with the Short column (In Progress
          // batches need less than a full batch).
          title: Head('Avail. for', 'How many of the batches left the stock covers: Available (approved stock left for this product after older plans) ÷ amount per batch, rounded down, and never more than the batches left. Red when it is not enough for the batches left.'),
          key: 'af', width: 92, align: 'center',
          sorter: (a, b) => (availFor(a) ?? -1) - (availFor(b) ?? -1),
          render: (_, r) => {
            const n = availFor(r);
            if (n === null) return <span style={{ color: C.mute }}>—</span>;
            return <span style={{ ...S.mono, fontSize: 12.5, fontWeight: 700, color: num(r.short_qty) > 0 ? C.red : C.green }}>{n}</span>;
          },
        },
      ],
    }] : []),
    {
      title: 'Stage', dataIndex: 'stage', key: 'stage', width: 110, ellipsis: true,
      sorter: (a, b) => String(a.stage || '').localeCompare(String(b.stage || '')),
      render: (v) => <span style={{ fontSize: 12.5, color: C.sub }}>{v || '—'}</span>,
    },
    { title: 'UOM', dataIndex: 'uom', key: 'uom', width: 58, align: 'center', render: (v) => <span style={{ fontSize: 11.5, color: C.sub }}>{v || '—'}</span> },
    q('Std/Batch', 'bmr_standard_qty', null, false, 'How much of this material ONE batch needs — when the BOM uses it at several stages (×2), the total of all of them. Left blank in Combined when products need different amounts.'),
    ...(showDone ? [
      q('Planned', 'planned_qty', null, false, 'Amount per batch × all planned batches.'),
      q('Dispensed', 'dispensed_qty', C.blue, true, 'Actual quantity issued to the dispensed batches: the stock card issues of every lot, added up.'),
    ] : []),
    q('Required', 'required_qty', C.ink, true, showDone
      ? 'Still needed: amount per batch × batches not yet dispensed.'
      : 'Total needed for all the batches: amount per batch × number of batches.'),
    q('Total Stock', 'total_stock_qty', C.sub, false, 'All the stock of this material in the store, approved or not. For information only.'),
    q('Approved', 'approved_qty', C.green, true, 'Stock that QC has approved. Only this stock can be used for production.'),
    ...(showReserved ? [
      {
        title: Head('Reserved', 'Approved stock already kept aside for older plans (or products added earlier in this plan). Click a number to see who has it.'),
        dataIndex: 'reserved_qty', key: 'reserved_qty', width: 96, align: 'right',
        sorter: (a, b) => num(a.reserved_qty) - num(b.reserved_qty),
        render: (v, r) => <ReservedCell value={v} by={r.reserved_by} />,
      },
      q('Available', 'available_qty', C.green, true, 'Approved stock left for this product: Approved − Reserved.'),
    ] : []),
    {
      title: <span style={{ fontSize: 12 }}>{Head('Other stock (not counted)', 'Stock that QC has not approved yet. Shown for information only — it cannot be used until approved.')}</span>,
      key: 'other',
      children: [
        q('Quar.', 'quarantine_qty', C.amber, false, 'Quarantine: received, but QC has not started checking it.'),
        q('U.Samp', 'under_sampling_qty', C.sub, false, 'Under sampling: waiting for QC to take a sample.'),
        q('U.Test', 'under_test_qty', C.sub, false, 'Under test: QC is testing the sample.'),
      ],
    },
    {
      title: Head('Short', 'How much more you need to buy. A green tick means there is enough.'), dataIndex: 'short_qty', key: 'short_qty', width: 104, align: 'right', fixed: 'right',
      defaultSortOrder: 'descend', sorter: (a, b) => num(a.short_qty) - num(b.short_qty),
      render: (v) => (num(v) > 0
        ? <span style={{ ...S.mono, fontSize: 12, fontWeight: 700, color: C.red }}>{fmt(v)}</span>
        : <CheckCircleFilled style={{ color: '#6ee7b7', fontSize: 12 }} />),
    },
  ];
  const COMPLETED_KEYS = ['material_code', 'material_name', 'stage', 'uom', 'bmr_standard_qty', 'required_qty'];
  const columns = completed ? allColumns.filter((c) => COMPLETED_KEYS.includes(c.key)) : allColumns;
  // Combined view: click a material to see which product needs how much.
  const hasBreakdown = !completed && data.some((r) => (r.breakdown || []).length);
  // Product view: a material the BOM uses on several lines opens to show them.
  const hasLines = !completed && !hasBreakdown && data.some((r) => (r.lines || []).length > 1);
  const expandable = hasBreakdown ? {
    expandedRowRender: (r) => <MaterialBreakdown row={r} />,
    rowExpandable: (r) => (r.breakdown || []).length > 0,
    expandRowByClick: true,
  } : hasLines ? {
    expandedRowRender: (r) => <LineBreakdown row={r} />,
    rowExpandable: (r) => (r.lines || []).length > 1,
    expandRowByClick: true,
  } : undefined;
  const hasRepeats = hasBreakdown ? data.some((r) => num(r.repeat_lines) > 1) : hasLines;

  return (
    <>
      {(shortCount > 0 || hasBreakdown || hasLines) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 8, flexWrap: 'wrap' }}>
          {shortCount > 0 && <>
            <Switch size="small" checked={shortOnly} onChange={setShortOnly} />
            <span style={{ fontSize: 12.5, color: C.sub }}>Show only the {shortCount} short material{shortCount === 1 ? '' : 's'}</span>
          </>}
          {(hasBreakdown || hasLines) && (
            <span style={{ fontSize: 12.5, color: C.blue, marginLeft: shortCount > 0 ? 10 : 0 }}>
              {hasBreakdown ? 'Click a material to see which product needs how much of it, and at which stage.' : 'Click + on a material to see every stage it is used in.'}
              {hasRepeats && <> Materials marked <Tag color="blue" style={{ marginInlineEnd: 0, fontSize: 11, lineHeight: '16px', padding: '0 4px' }}>×2</Tag> are used more than once in a BOM.</>}
            </span>
          )}
        </div>
      )}
      <Table
        columns={columns} dataSource={data} loading={loading} bordered size="small" expandable={expandable}
        showSorterTooltip={false}
        pagination={data.length > 40 ? { pageSize, onChange: (_, size) => setPageSize(size), showSizeChanger: true, pageSizeOptions: ['20', '40', '100'], size: 'small', showTotal: (t, r) => `${r[0]}-${r[1]} of ${t}` } : false}
        scroll={{ x: 'max-content' }} sticky={{ offsetHeader: 0 }}
        rowClassName={(r) => (!completed && num(r.short_qty) > 0 ? 'mm-short' : '')}
        locale={{ emptyText: <div style={{ padding: '18px 0' }}><Empty description={<span style={{ fontSize: 12 }}>{emptyText || 'No materials'}</span>} image={Empty.PRESENTED_IMAGE_SIMPLE} /></div> }}
      />
      <style>{`
        .mm-short > td { background-color: ${C.redBg} !important; }
        .mm-short:hover > td { background-color: #fee2e2 !important; }
        .mm-tight .ant-table-thead > tr > th { padding: 6px 8px !important; font-size: 12px !important; }
        .mm-tight .ant-table-tbody > tr > td { padding: 5px 8px !important; }
        .mm-tight .ant-tabs-nav { margin-bottom: 10px !important; }
      `}</style>
    </>
  );
};

/**
 * A saved plan's Completed tab: just which batches are done - batch number
 * and product - with a search on the batch number (any part of it, any case).
 */
const CompletedBatches = ({ batches }) => {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const q = search.trim().toLowerCase();
  const data = useMemo(() => batches
    .filter((b) => !q || String(b.product_batch_number || '').toLowerCase().includes(q))
    .map((b) => ({ ...b, key: b.id })), [batches, q]);
  // The page is kept here so # counts on across pages instead of restarting
  // at 1, and a new search starts again from page 1.
  const paged = data.length > 40;
  const current = paged ? Math.min(page, Math.ceil(data.length / 40)) : 1;
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <Input size="small" allowClear placeholder="Search batch number" prefix={<SearchOutlined style={{ color: C.mute }} />}
          value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} style={{ width: 240 }} />
        {q && <span style={S.hint}>{data.length} of {batches.length} batches</span>}
      </div>
      <Table size="small" bordered showSorterTooltip={false} dataSource={data}
        pagination={paged ? { current, onChange: setPage, pageSize: 40, showSizeChanger: false, size: 'small' } : false}
        locale={{ emptyText: <Empty description={<span style={{ fontSize: 12.5 }}>No completed batch number contains "{search.trim()}"</span>} image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
        columns={[
          { title: '#', key: 'n', width: 50, align: 'center', render: (_, __, i) => <span style={{ fontSize: 12, color: C.mute }}>{(current - 1) * 40 + i + 1}</span> },
          {
            title: 'Batch Number', dataIndex: 'product_batch_number', key: 'product_batch_number', width: 200,
            sorter: (a, b) => String(a.product_batch_number || '').localeCompare(String(b.product_batch_number || '')),
            render: (v) => <span style={{ ...S.mono, fontSize: 12.5, fontWeight: 700, color: C.ink }}>{v || '—'}</span>,
          },
          {
            title: 'Product Name', dataIndex: 'product_name', key: 'product_name',
            sorter: (a, b) => String(a.product_name || '').localeCompare(String(b.product_name || '')),
            render: (v) => <span style={{ fontSize: 12.5 }}>{v || '—'}</span>,
          },
        ]} />
    </>
  );
};

/* ================================================================== *
 *  The plan's own BOM — which lines of each product's BOM it needs
 * ================================================================== */
/** How many of a product's BOM lines are ticked: `value` null = all of them. */
const tickedCount = (lines, value) => (Array.isArray(value) ? value.length : (lines || []).length);
/** Comparable form of a selection: the sorted ids, or 'all' for the full BOM. */
const ticksKey = (v) => (Array.isArray(v) ? [...v].sort((a, b) => a - b).join(',') : 'all');

/**
 * One product's BOM as a checklist: every line, grouped by stage and
 * dispensing sequence in BMR order. A sequence's box ticks or unticks all its
 * materials at once; each material can be ticked on its own. `value` is the
 * ticked bom_line_ids, or null for every line (the full BOM). Likely vendor
 * alternates - same stage, sequence and quantity, different code - are tagged:
 * usually only one of them is dispensed. For completing a batch ticked
 * alternates are either/or, but Required counts every ticked one.
 */
const BomPicker = ({ lines, value, onChange, loading, error }) => {
  if (loading) return <div style={{ padding: 24, textAlign: 'center' }}><Spin size="small" /></div>;
  if (error) return <Alert type="error" showIcon message={<span style={{ fontSize: 12 }}>{error}</span>} />;
  if (!(lines || []).length) return <Empty description={<span style={{ fontSize: 12.5 }}>This product has no BOM configured</span>} image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  const all = lines.map((l) => l.bom_line_id);
  const ticked = new Set(Array.isArray(value) ? value : all);
  const set = (next) => onChange(next.size === all.length ? null : all.filter((id) => next.has(id)));
  const toggle = (ids, on) => {
    const next = new Set(ticked);
    ids.forEach((id) => (on ? next.add(id) : next.delete(id)));
    set(next);
  };
  const groups = [];
  const byKey = new Map();
  lines.forEach((l) => {
    const k = `${l.stage || ''}|${l.dispensing_sequence ?? ''}`;
    if (!byKey.has(k)) { byKey.set(k, { key: k, stage: l.stage, seq: l.dispensing_sequence, lines: [] }); groups.push(byKey.get(k)); }
    byKey.get(k).lines.push(l);
  });
  return (
    <div style={{ maxWidth: 920 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: ticked.size ? C.ink : C.red }}>
          {ticked.size} of {all.length} BOM line{all.length === 1 ? '' : 's'} ticked
        </span>
        <Button size="small" type="link" style={{ padding: 0 }} disabled={ticked.size === all.length} onClick={() => onChange(null)}>Tick all</Button>
        <Button size="small" type="link" style={{ padding: 0 }} disabled={!ticked.size} onClick={() => onChange([])}>Untick all</Button>
        {!ticked.size && <span style={{ fontSize: 12, color: C.red }}>Tick at least one material — this product cannot be planned with none.</span>}
      </div>
      <div style={{ border: `1px solid ${C.line}`, borderRadius: 6 }}>
        {groups.map((g, gi) => {
          const ids = g.lines.map((l) => l.bom_line_id);
          const on = ids.filter((id) => ticked.has(id)).length;
          const alt = g.lines.some((l) => (l.alternate_codes || []).length);
          return (
            <div key={g.key} style={{ borderTop: gi ? `1px solid ${C.line}` : 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 10px', backgroundColor: '#f8fafc', flexWrap: 'wrap' }}>
                <Checkbox checked={on === ids.length} indeterminate={on > 0 && on < ids.length} onChange={(e) => toggle(ids, e.target.checked)}>
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: C.ink }}>{g.seq != null ? `Seq ${g.seq}` : 'No sequence'}</span>
                </Checkbox>
                <span style={{ fontSize: 12, color: C.sub }}>{g.stage || '—'}</span>
                <span style={{ fontSize: 11.5, color: C.mute }}>{on}/{ids.length} ticked</span>
                {alt && <Tag color="gold" style={{ marginInlineEnd: 0, fontSize: 11, lineHeight: '16px' }}>alternates</Tag>}
              </div>
              {g.lines.map((l) => (
                <div key={l.bom_line_id} className="mm-bom-line" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 10px 3px 34px', flexWrap: 'wrap', opacity: ticked.has(l.bom_line_id) ? 1 : 0.55 }}>
                  <Checkbox checked={ticked.has(l.bom_line_id)} onChange={(e) => toggle([l.bom_line_id], e.target.checked)} />
                  <span style={{ ...S.mono, fontSize: 12.5, fontWeight: 600, color: C.ink, minWidth: 104 }}>{l.material_code || '—'}</span>
                  <span style={{ fontSize: 12.5, color: C.ink, flex: '1 1 220px', minWidth: 0 }}>
                    {l.material_name || '—'}
                    {l.has_invalid_std_qty && (
                      <Tooltip title="The amount per batch for this line is missing in the BOM, so it is counted as 0. Please check the BOM.">
                        <WarningFilled style={{ color: C.amber, fontSize: 12, marginLeft: 5 }} />
                      </Tooltip>
                    )}
                  </span>
                  <span style={{ ...S.mono, fontSize: 12.5, color: C.sub, whiteSpace: 'nowrap' }}>{fmt(l.bmr_standard_qty)} {l.uom || ''}<span style={{ color: C.mute }}> /batch</span></span>
                  {(l.alternate_codes || []).length > 0 && (
                    <Tooltip styles={{ root: { maxWidth: 320 } }} title={`Possible alternate of ${l.alternate_codes.join(', ')} — same stage, sequence and quantity. Usually only one of them is dispensed, and a batch is complete once any one ticked alternate is issued. Required counts every ticked alternate, so untick the ones that will not be used to see the true shortage.`}>
                      <Tag color="gold" style={{ marginInlineEnd: 0, fontSize: 11, lineHeight: '16px', cursor: 'help' }}>possible alternate</Tag>
                    </Tooltip>
                  )}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * A saved plan's In Progress tab: batches that have started dispensing but
 * have not been issued every ticked material yet - what each is still waiting
 * for, and what it was issued that the plan did not tick (often the other
 * vendor's alternate, which is usually why). A batch completes on its own
 * once the last ticked material is issued; one waiting for something that
 * will never be issued is released by editing the plan and unticking it.
 */
const InProgressBatches = ({ batches }) => {
  const chip = (key, code, tip, color) => (
    <Tooltip key={key} title={tip}>
      <span style={{ ...S.mono, fontSize: 12, padding: '0 6px', borderRadius: 4, border: `1px solid ${color === C.red ? '#fecaca' : C.line}`, color: color || C.sub, backgroundColor: color === C.red ? C.redBg : '#f8fafc', cursor: 'help', whiteSpace: 'nowrap' }}>{code || '—'}</span>
    </Tooltip>
  );
  return (
    <>
      <div style={{ ...S.hint, marginBottom: 8 }}>
        These batches have started dispensing. Each is completed on its own once every ticked material has been issued to it on the stock card.
        {' '}If a batch waits for a material that will never be issued (for example, the other vendor's alternate was used), <b>Edit</b> the plan and untick it.
      </div>
      <Table size="small" bordered pagination={batches.length > 40 ? { pageSize: 40, showSizeChanger: false, size: 'small' } : false}
        dataSource={batches.map((b) => ({ ...b, key: b.id }))}
        columns={[
          { title: '#', key: 'n', width: 44, align: 'center', render: (_, __, i) => <span style={{ fontSize: 12, color: C.mute }}>{i + 1}</span> },
          { title: 'Batch Number', dataIndex: 'product_batch_number', key: 'no', width: 150, render: (v) => <span style={{ ...S.mono, fontSize: 12.5, fontWeight: 700, color: C.ink }}>{v || '—'}</span> },
          { title: 'Product', dataIndex: 'product_name', key: 'product', width: 240, render: (v) => <span style={{ fontSize: 12.5 }}>{v || '—'}</span> },
          { title: 'Started', dataIndex: 'started_date', key: 'started', width: 90, render: (v) => <span style={{ fontSize: 12.5, color: C.sub }}>{fmtDate(v)}</span> },
          {
            title: Head('Issued', 'Ticked BOM lines issued to this batch so far. A line counts once its stage got a quantity; where Nucleus does not record the stage, any issue of the material counts; and once one ticked alternate is issued, the others of its sequence count too.'),
            key: 'issued', width: 90, align: 'center',
            render: (_, b) => {
              const lines = b.lines || [];
              const done = lines.filter((l) => l.issued).length;
              return <span style={{ ...S.mono, fontSize: 12.5, fontWeight: 700, color: done === lines.length ? C.green : C.amber }}>{done}/{lines.length}</span>;
            },
          },
          {
            title: 'Still waiting for', key: 'missing',
            render: (_, b) => {
              const missing = (b.lines || []).filter((l) => !l.issued);
              return missing.length ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {missing.map((l) => chip(l.bom_line_id, l.material_code, `${l.material_name || ''} · ${l.stage || '—'}${l.dispensing_sequence != null ? ` · seq ${l.dispensing_sequence}` : ''}`, C.red))}
                </div>
              ) : <span style={{ fontSize: 12, color: C.green }}>Nothing — completes on the next refresh</span>;
            },
          },
          {
            title: Head('Issued, not ticked', 'Materials issued to this batch that the plan did not tick — often the alternate that was used instead of the ticked one.'),
            key: 'extra', width: 220,
            render: (_, b) => ((b.not_ticked_issued || []).length ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {b.not_ticked_issued.map((m) => chip(m.material_id, m.material_code, `${m.material_name || ''} · ${fmt(m.quantity_issued)} ${m.uom || ''} issued`))}
              </div>
            ) : <span style={{ color: '#cbd5e1' }}>—</span>),
          },
        ]} />
    </>
  );
};

/* ================================================================== *
 *  Module
 * ================================================================== */
/**
 * One empty product line of the plan form. bom_line_ids: the BOM lines ticked
 * for the product (null = every line). start_date ('YYYY-MM-DD') and
 * batches_per_day: the planner's schedule; the end date is worked out from
 * them (endDateFor), never typed.
 */
const blankItem = (key = Date.now()) => ({
  key, family: null, product_id: null, batches: 1, bom_line_ids: null,
  start_date: null, batches_per_day: null,
});
/** The schedule fields the planner types. */
const SCHEDULE_KEYS = ['start_date', 'batches_per_day'];
/**
 * The end date, as the server works it out: the day the last batch runs
 * (daysFor - 10 batches at 3/day = 4 days, at 1.5/day = 7). null without a
 * start date or batches/day.
 */
const endDateFor = (start_date, batches_per_day, batches) => (start_date && Math.round(num(batches_per_day) * 2) > 0 && num(batches) > 0
  ? dayjs(start_date).add(daysFor(batches, batches_per_day) - 1, 'day').format('YYYY-MM-DD')
  : null);
/** A form line with its worked-out end date, for scheduleText. */
const withEnd = (it) => ({ ...it, end_date: endDateFor(it.start_date, it.batches_per_day, it.batches) });
/** "15 Oct 26 → 19 Oct 26 · 3 batches/day", or '' when nothing is filled in. */
const scheduleText = (s) => {
  if (!s || SCHEDULE_KEYS.every((k) => s[k] == null)) return '';
  // dayjs reads 'YYYY-MM-DD' as a local date; new Date() would read UTC midnight.
  const day = (v) => (v ? dayjs(v).format('DD MMM YY') : '—');
  const dates = s.start_date || s.end_date ? `${day(s.start_date)} → ${day(s.end_date)}` : '';
  const perDay = s.batches_per_day ? `${s.batches_per_day} batch${s.batches_per_day === 1 ? '' : 'es'}/day` : '';
  return [dates, perDay].filter(Boolean).join(' · ');
};

const MaterialManagement = () => {
  // Saved Plans is the landing view - opening the module usually means
  // checking on work already planned, not starting something new.
  const [view, setView] = useState('list');
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  // bom_line_ids: the BOM lines ticked for the product - null = every line.
  const [items, setItems] = useState([blankItem(1)]);
  const [planName, setPlanName] = useState('');
  // Set while the planner is editing a saved plan: { plan_id, plan_name }.
  // The same form is used as for a new plan; existing products come in
  // `locked` (their document cannot change) with `completed` = batches
  // already dispensed and `started` = dispensed + in progress, which is the
  // floor for their batch count.
  const [editing, setEditing] = useState(null);
  // Each picked product's BOM lines, for the material checklist, fetched once
  // per product: { [product_id]: { loading, lines, error } }.
  const [boms, setBoms] = useState({});
  const bomRequested = useRef(new Set());
  const [results, setResults] = useState({});
  const [reqLoading, setReqLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [statusDownloading, setStatusDownloading] = useState(false);

  // Plan download in the planners' "Upto OCT RM" workbook layout.
  const handleDownload = useCallback(async (planId) => {
    setDownloading(true);
    try {
      const data = await fetchPlanExport(planId);
      if (!data.rows || !data.rows.length) { message.info('Nothing to download — every batch of this plan is already dispensed.'); return; }
      await downloadPlanWorkbook(data);
      message.success(`Downloaded ${data.rows.length} material lines`);
    } catch (e) { message.error(e.message || 'Could not prepare the download'); }
    finally { setDownloading(false); }
  }, []);

  // Every batch of the plan on its schedule, green / red by material. The plan
  // is read again first, so the stock is as of the click (and the screen
  // shows the same figures as the file).
  const handleStatusDownload = useCallback(async (planId) => {
    setStatusDownloading(true);
    try {
      const d = await fetchPlanDetail(planId);
      setDetail(d);
      const rows = buildMaterialStatus(d, (id) => {
        const p = products.find((x) => x.id === id);
        return p && p.bmr_bpr_code && p.bmr_bpr_code !== 'N/A' ? p.bmr_bpr_code : null;
      }, (id) => products.find((x) => x.id === id)?.product_family || null);
      if (!rows.length) { message.info('This plan has no batches to show.'); return; }
      await downloadMaterialStatusWorkbook(d, rows);
      const short = rows.filter((r) => r.kind === 'short').length;
      message.success(`Downloaded ${rows.length} batch${rows.length === 1 ? '' : 'es'} — ${short} short of material`);
    } catch (e) { message.error(e.message || 'Could not prepare the material status'); }
    finally { setStatusDownloading(false); }
  }, [products]);

  const loadProducts = useCallback(async () => {
    setProductsLoading(true);
    try { setProducts(await fetchProducts()); }
    catch (e) { message.error(e.message || 'Could not load products'); }
    finally { setProductsLoading(false); }
  }, []);
  useEffect(() => { loadProducts(); }, [loadProducts]);

  // The same drug appears many times - once per BMR/BPR document and once per
  // revision of it - so the list is grouped by product family (is_product, e.g.
  // ATS = Atorvastatin) and the document code is part of each label. Flat and
  // unlabelled, the entries look like meaningless duplicates.
  /* Two-step product picker.
   *
   * Step 1 is the product family (product_details.is_product, e.g. ATS =
   * Atorvastatin). Step 2 is the specific BMR/BPR document within it. A drug
   * carries one entry per document AND one per revision of it - 71 rows for
   * Atorvastatin alone - so a single flat list is unusable.
   *
   * Note this vocabulary is is_product, NOT the short codes on the Create Plan
   * screen (which come from product_option_volume_masters and use ATR where
   * this uses ATS). The two share only FAMO and FURO, and nothing maps between
   * them, so they cannot be made to agree without a new mapping table.
   */
  const familyOptions = useMemo(() => {
    const g = new Map();
    products.forEach((p) => {
      const fam = p.product_family || null;
      if (!fam) return;                       // 639 unclassified rows, none have a BOM
      if (!g.has(fam)) g.set(fam, { total: 0, usable: 0 });
      const e = g.get(fam);
      e.total += 1;
      if (p.has_bom !== false) e.usable += 1;
    });
    return [...g.entries()]
      .filter(([, e]) => e.usable > 0)        // a family with no BOM anywhere is a dead end
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([fam, e]) => ({
        value: fam,
        label: `${fam}  (${e.usable})`,
        search: fam.toLowerCase(),
      }));
  }, [products]);

  // `takenIds` = documents already on another line of this plan. They are
  // disabled rather than hidden so the planner sees why: one line per
  // document, and more batches go on that line.
  const docOptionsFor = useCallback((family, takenIds = new Set()) => {
    if (!family) return [];
    return products
      .filter((p) => (p.product_family || null) === family)
      .map((p) => {
        const code = (p.bmr_bpr_code && p.bmr_bpr_code !== 'N/A') ? p.bmr_bpr_code : null;
        const taken = takenIds.has(p.id);
        return {
          value: p.id,
          disabled: p.has_bom === false || taken,
          // Plain-text label is what the closed select shows; the two-line
          // layout in the open list comes from optionRender below.
          label: [code, p.name].filter(Boolean).join(' · '),
          code,
          name: p.name,
          type: p.bmr_bpr_type,
          meta: taken ? 'already in this plan — add batches there'
            : p.has_bom === false ? 'no BOM' : `${p.bom_material_count} material${p.bom_material_count === 1 ? '' : 's'}`,
          search: `${p.id} ${p.name} ${p.bmr_bpr_code || ''}`.toLowerCase(),
        };
      });
  }, [products]);

  const filterProduct = useCallback(
    (input, option) => (option?.search || '').includes(String(input).toLowerCase().trim()),
    []
  );
  const productById = useCallback((id) => products.find((p) => p.id === id) || null, [products]);

  useEffect(() => {
    items.forEach(({ product_id: id }) => {
      if (!id || bomRequested.current.has(id)) return;
      bomRequested.current.add(id);
      setBoms((b) => ({ ...b, [id]: { loading: true, lines: [] } }));
      fetchProductBom(id)
        .then((lines) => setBoms((b) => ({ ...b, [id]: { loading: false, lines: lines || [] } })))
        .catch((e) => {
          bomRequested.current.delete(id);          // picking it again retries
          setBoms((b) => ({ ...b, [id]: { loading: false, lines: [], error: e.message || 'Could not load the BOM' } }));
        });
    });
  }, [items]);

  // Every line with a product picked; a product with nothing ticked stays
  // here (its checklist must stay reachable) but cannot be calculated or saved.
  const pickedItems = useMemo(() => items.filter((i) => i.product_id), [items]);
  const untickedItems = useMemo(() => pickedItems.filter((i) => Array.isArray(i.bom_line_ids) && !i.bom_line_ids.length), [pickedItems]);
  const validItems = useMemo(() => items.filter((i) => i.product_id && i.batches >= 1
    && !(Array.isArray(i.bom_line_ids) && !i.bom_line_ids.length)), [items]);

  // Only the newest preview request may update the screen: answers can come
  // back out of order, and an older, slower one must not overwrite a newer.
  const reqSeq = useRef(0);
  const loadRequirements = useCallback(async () => {
    const seq = ++reqSeq.current;
    if (!validItems.length) { setResults({}); setReqLoading(false); return; }
    setReqLoading(true);
    try {
      // One call for the whole plan: the products share stock with each other
      // and with every saved plan, so they cannot be calculated one by one.
      // When editing, the plan is previewed at its own place in the queue.
      // bom_line_ids goes only where it differs from what the server assumes
      // when it is left out - the full BOM, or on an edit the product's saved
      // ticks. The whole plan travels in the URL: sending it for every product
      // took the 119-product "New Oct" edit from 6.3 KB to 9.7 KB, past the
      // 8 KB request line a default nginx accepts.
      const saved = new Map((editing?.original?.items || []).map((o) => [o.product_id, o.bom_line_ids]));
      const res = await fetchRequirement(validItems.map((i) => ({
        product_id: i.product_id,
        batches: i.batches,
        ...(ticksKey(i.bom_line_ids) === ticksKey(saved.get(i.product_id)) ? {} : { bom_line_ids: i.bom_line_ids ?? null }),
      })), editing?.plan_id);
      if (seq !== reqSeq.current) return;
      const out = {};
      (res.products || []).forEach((p) => { out[p.product.id] = p; });
      setResults(out);
    } catch (e) { if (seq === reqSeq.current) message.error(e.message || 'Could not calculate requirement'); }
    finally { if (seq === reqSeq.current) setReqLoading(false); }
  }, [validItems, editing]);
  // Wait for typing to pause: "34" is two keystrokes, and each one used to
  // start a full recalculation. The refresh button still runs it at once.
  useEffect(() => {
    const t = setTimeout(loadRequirements, 450);
    return () => clearTimeout(t);
  }, [loadRequirements]);

  const combined = useMemo(() => mergeMaterials(
    validItems.map((i) => results[i.product_id]?.materials),
    // Batches that still need material (dispensed ones of an edited plan need none).
    validItems.map((i) => ({ product_id: i.product_id, name: productById(i.product_id)?.name || i.product_name, batches: i.batches - (i.completed || 0) })),
  ), [validItems, results, productById]);
  // Batches still to run - dispensed batches of an edited plan need nothing.
  const totalBatches = useMemo(() => validItems.reduce((s, i) => s + i.batches - (i.completed || 0), 0), [validItems]);
  const batchesPossible = useMemo(() => {
    const v = validItems.map((i) => results[i.product_id]?.summary?.batches_possible).filter((x) => x !== undefined);
    return v.length ? Math.min(...v) : '—';
  }, [validItems, results]);

  // Saved plan: products are returned in queue order, so mergeMaterials takes
  // Reserved from the first product - what earlier saved plans hold.
  // Every live row also carries what its product has already dispensed,
  // including what In Progress batches have been issued so far.
  const detailDone = useMemo(() => dispensedOf(detail?.completed_batches, detail?.in_progress_batches), [detail]);
  const detailProducts = useMemo(() => (detail?.products || []).map((p) => ({
    ...p,
    done: detailDone.get(p.product_id)?.batches || 0,
    live_materials: withDispensed(p.live_materials, detailDone.get(p.product_id)),
  })), [detail, detailDone]);
  const detailCombined = useMemo(() => {
    const rows = mergeMaterials(
      detailProducts.map((p) => p.live_materials),
      detailProducts.map((p) => ({ product_id: p.product_id, name: p.product_name, batches: p.running, done: p.done })),
    );
    // A product with every batch dispensed is not in the live list (it needs
    // nothing more), but still belongs in "who dispensed how much". It is
    // added to the breakdown only - it takes no stock, so stock and short are
    // untouched. A material used by such products alone has nothing left to
    // procure and is not listed.
    const live = new Set(detailProducts.map((p) => p.product_id));
    const byId = new Map(rows.map((r) => [r.material_id, r]));
    detailDone.forEach((d, product_id) => {
      if (live.has(product_id)) return;
      d.byMaterial.forEach((m, material_id) => {
        const r = byId.get(material_id);
        if (!r) return;
        r.dispensed_qty = num(r.dispensed_qty) + m.qty;
        r.planned_qty = num(r.planned_qty) + m.stdQty;
        r.repeat_lines = Math.max(num(r.repeat_lines), (m.frozenLines || []).length);
        const head = { product_id, product_name: d.name, batches: 0, planned_batches: d.batches, dispensed_batches: d.batches, required_qty: 0, allocated_qty: 0, short_qty: 0 };
        // Frozen with its BOM lines: one breakdown line per line and the
        // Dispensed per stage, exactly like a live product (stageSlots).
        if ((m.frozenLines || []).length) {
          const { lines, dispense_rest: rest } = stageSlots(
            m.frozenLines.map((l) => ({ ...l, required_qty: 0, short_qty: 0 })), m, d.batchList, m.stdQty,
          );
          const span = lines.length + (rest ? 1 : 0);
          lines.forEach((l, i) => r.breakdown.push({
            ...head, span: i === 0 ? span : 0, multi_line: lines.length > 1, dispensed_lines: i === 0 ? m.lines : [],
            stage: l.stage || null, dispensing_sequence: l.dispensing_sequence ?? null, bmr_standard_qty: num(l.bmr_standard_qty),
            planned_qty: l.planned_qty, dispense: l.dispense, dispensed_qty: num(l.dispense.qty),
          }));
          if (rest) {
            r.breakdown.push({
              ...head, span: 0, multi_line: true, is_rest: true, dispensed_lines: [], stage: null, bmr_standard_qty: 0,
              planned_qty: rest.planned, dispensed_qty: rest.qty, dispense: { span: 1, qty: rest.qty, issued: rest.issued, missing: [] },
            });
          }
          return;
        }
        r.breakdown.push({
          ...head, span: 1, dispensed_lines: m.lines,
          stage: m.stage, bmr_standard_qty: m.std, planned_qty: m.stdQty, dispensed_qty: m.qty,
          dispense: { span: 1, qty: m.qty, issued: m.lines.filter((x) => x.qty !== 0), missing: m.lines.filter((x) => x.qty === 0) },
        });
      });
    });
    return rows;
  }, [detailProducts, detailDone]);

  const addItem = () => setItems((p) => [...p, blankItem()]);
  const removeItem = (k) => setItems((p) => (p.length === 1 ? p : p.filter((i) => i.key !== k)));
  const patchItem = (k, patch) => setItems((p) => p.map((i) => (i.key === k ? { ...i, ...patch } : i)));

  const loadPlans = useCallback(async () => {
    setPlansLoading(true);
    try { setPlans(await fetchPlans()); }
    catch (e) { message.error(e.message || 'Could not load plans'); }
    finally { setPlansLoading(false); }
  }, []);

  const openPlan = useCallback(async (id) => {
    setView('detail'); setDetailLoading(true);
    try { setDetail(await fetchPlanDetail(id)); }
    catch (e) { message.error(e.message || 'Could not load plan'); setDetail(null); }
    finally { setDetailLoading(false); }
  }, []);

  const resetForm = useCallback(() => {
    setEditing(null);
    setItems([blankItem()]);
    setPlanName('');
    setResults({});
  }, []);

  // Load a saved plan into the form. Every product it holds comes in, even
  // one whose batches are all dispensed - leaving it out would ask the
  // server to remove it, which is refused.
  const startEdit = useCallback((d) => {
    // An older backend does not send plan_items; editing would open an empty
    // form with nothing to change, so say why instead.
    if (!Array.isArray(d.plan_items) || !d.plan_items.length) {
      message.error({ content: 'This plan cannot be edited yet — the server is running an older version. Please restart the backend and reload.', duration: 8 });
      return;
    }
    setEditing({
      plan_id: d.plan_id,
      plan_name: d.plan_name,
      // What the plan looked like when opened, to show what the edit changes.
      original: {
        plan_name: d.plan_name || '',
        items: d.plan_items.map((pi) => ({
          product_id: pi.product_id, product_name: pi.product_name, batches: pi.no_of_batches, bom_line_ids: pi.bom_line_ids ?? null,
          start_date: pi.start_date ?? null, batches_per_day: pi.batches_per_day ?? null,
        })),
      },
    });
    setPlanName(d.plan_name || '');
    setItems((d.plan_items || []).map((pi) => ({
      key: `p${pi.product_id}`,
      family: products.find((p) => p.id === pi.product_id)?.product_family || null,
      product_id: pi.product_id,
      product_name: pi.product_name,
      product_code: pi.product_code,
      batches: pi.no_of_batches,
      bom_line_ids: pi.bom_line_ids ?? null,
      start_date: pi.start_date ?? null,
      batches_per_day: pi.batches_per_day ?? null,
      completed: pi.completed,
      in_progress: pi.in_progress || 0,
      started: pi.completed + (pi.in_progress || 0),
      locked: true,
    })));
    setResults({});
    setView('new');
  }, [products]);

  // Every difference between the form and the plan as it was opened, in
  // plain words ("Bupropion 150 mg: 26 → 28"). Empty = nothing to save.
  const editChanges = useMemo(() => {
    if (!editing?.original) return [];
    const short = (s) => (s && s.length > 38 ? `${s.slice(0, 36)}…` : s || 'Product');
    const orig = editing.original;
    const out = [];
    if ((planName || '').trim() !== (orig.plan_name || '').trim()) out.push({ kind: 'name', text: `Name: "${orig.plan_name || 'Untitled'}" → "${planName.trim() || 'Untitled'}"` });
    // Picked, not just valid: a product with nothing ticked is a change to
    // its ticks, not a removal.
    const now = new Map(pickedItems.map((i) => [i.product_id, i]));
    orig.items.forEach((o) => {
      const c = now.get(o.product_id);
      if (!c) { out.push({ kind: 'remove', text: `Remove ${short(o.product_name)} (${o.batches} batch${o.batches === 1 ? '' : 'es'})` }); return; }
      if (c.batches !== o.batches) out.push({ kind: c.batches > o.batches ? 'up' : 'down', text: `${short(o.product_name)}: ${o.batches} → ${c.batches}` });
      if (ticksKey(c.bom_line_ids) !== ticksKey(o.bom_line_ids)) {
        const lines = boms[o.product_id]?.lines;
        out.push({ kind: 'materials', text: `${short(o.product_name)}: ticked materials changed${lines ? ` (${tickedCount(lines, o.bom_line_ids)} → ${tickedCount(lines, c.bom_line_ids)} of ${lines.length} lines)` : ''}` });
      }
      if (SCHEDULE_KEYS.some((k) => (c[k] ?? null) !== (o[k] ?? null))) {
        out.push({ kind: 'schedule', text: `${short(o.product_name)}: schedule ${scheduleText(withEnd(o)) || 'none'} → ${scheduleText(withEnd(c)) || 'none'}` });
      }
    });
    pickedItems.forEach((i) => {
      if (orig.items.some((o) => o.product_id === i.product_id)) return;
      out.push({ kind: 'add', text: `Add ${short(productById(i.product_id)?.name || i.product_name)} × ${i.batches}` });
    });
    return out;
  }, [editing, planName, pickedItems, productById, boms]);

  // Leaving an edit with unsaved changes asks first; with none, it just goes.
  const confirmDiscard = useCallback((go) => {
    if (!editing || !editChanges.length) { go(); return; }
    Modal.confirm({
      title: 'Discard your changes?',
      content: `${editChanges.length} unsaved change${editChanges.length === 1 ? '' : 's'} to this plan will be lost.`,
      okText: 'Discard', okButtonProps: { danger: true }, cancelText: 'Keep editing',
      onOk: go,
    });
  }, [editing, editChanges]);

  const cancelEdit = useCallback(() => {
    confirmDiscard(() => {
      const id = editing?.plan_id;
      resetForm();
      if (id) openPlan(id); else setView('list');
    });
  }, [editing, resetForm, openPlan, confirmDiscard]);

  const startNewPlan = useCallback(() => {
    if (!editing) { setView('new'); return; }
    confirmDiscard(() => { resetForm(); setView('new'); });
  }, [editing, resetForm, confirmDiscard]);

  const goToSavedPlans = useCallback(() => {
    if (!editing) { setView('list'); return; }
    confirmDiscard(() => { resetForm(); setView('list'); });
  }, [editing, resetForm, confirmDiscard]);

  const handleSave = useCallback(async () => {
    if (!validItems.length && !untickedItems.length) { message.warning('Pick at least one product'); return; }
    if (untickedItems.length) {
      message.warning(`Tick at least one material for ${untickedItems.map((i) => productById(i.product_id)?.name || i.product_name || 'each product').join(', ')}`);
      return;
    }
    if (editing && !editChanges.length) { message.info('Nothing has changed yet'); return; }
    setSaving(true);
    try {
      const body = {
        plan_name: planName || null,
        items: validItems.map((i) => ({
          product_id: i.product_id, no_of_batches: i.batches, bom_line_ids: i.bom_line_ids ?? null,
          start_date: i.start_date ?? null, batches_per_day: i.batches_per_day ?? null,
        })),
      };
      if (editing) {
        const done = await updatePlan(editing.plan_id, body);
        const ticks = [
          done.selections_changed ? `materials changed for ${done.selections_changed} product${done.selections_changed === 1 ? '' : 's'}` : '',
          done.schedules_changed ? `schedule changed for ${done.schedules_changed} product${done.schedules_changed === 1 ? '' : 's'}` : '',
        ].filter(Boolean).map((x) => `, ${x}`).join('');
        message.success(`Plan updated — ${done.batches_added} batch${done.batches_added === 1 ? '' : 'es'} added, ${done.batches_removed} removed${ticks}`);
        const id = editing.plan_id;
        resetForm(); openPlan(id);
      } else {
        const made = await createPlan(body);
        message.success(`Plan created — ${made.rows_created} batches`);
        setPlanName(''); openPlan(made.plan_id);
      }
    } catch (e) { message.error({ content: e.message || 'Could not save plan', duration: 8 }); }
    finally { setSaving(false); }
  }, [validItems, untickedItems, productById, planName, editing, editChanges, openPlan, resetForm]);

  const handleDelete = useCallback(async (id) => {
    try { await deletePlan(id); message.success('Plan deleted'); loadPlans(); }
    catch (e) { message.error(e.message || 'Could not delete plan'); }
  }, [loadPlans]);

  useEffect(() => { if (view === 'list') loadPlans(); }, [view, loadPlans]);

  const planColumns = [
    {
      title: 'Plan', dataIndex: 'plan_id', key: 'plan_id', width: 210,
      render: (v, r) => (
        <>
          <Button type="link" style={{ padding: 0, height: 'auto', fontWeight: 700, fontSize: 12.5 }} onClick={(e) => { e.stopPropagation(); openPlan(v); }}>
            {r.plan_name || 'Untitled plan'}
          </Button>
          <div style={{ ...S.mono, fontSize: 11, color: C.mute }}>{v}</div>
        </>
      ),
    },
    {
      title: 'Products', dataIndex: 'product_list', key: 'product_list',
      render: (list = []) => (
        <div style={{ fontSize: 12.5, lineHeight: 1.5 }}>
          {list.slice(0, 2).map((p, i) => <div key={i}>{p.name} <span style={{ color: C.mute }}>× {p.batches}</span></div>)}
          {list.length > 2 && <span style={{ color: C.mute }}>+{list.length - 2} more</span>}
        </div>
      ),
    },
    { title: 'Batches', dataIndex: 'total_batches', key: 'total_batches', width: 72, align: 'center', render: (v) => <span style={{ ...S.mono, fontWeight: 700 }}>{v}</span> },
    {
      title: 'Progress', key: 'progress', width: 160,
      render: (_, r) => (
        <>
          <Progress percent={r.total_batches ? Math.round((r.completed / r.total_batches) * 100) : 0} size="small" strokeColor={C.green} showInfo={false} />
          <div style={{ fontSize: 11.5, color: C.sub }}>
            <b style={{ color: C.green }}>{r.completed}</b> done
            {r.in_progress > 0 && <> · <b style={{ color: C.amber }}>{r.in_progress}</b> in progress</>}
            {' · '}<b style={{ color: C.blue }}>{r.running}</b> running
          </div>
        </>
      ),
    },
    { title: 'Created', dataIndex: 'createdAt', key: 'createdAt', width: 92, render: (v) => <span style={{ fontSize: 12.5, color: C.sub }}>{fmtDate(v)}</span> },
    {
      title: '', key: 'a', width: 44, align: 'center',
      render: (_, r) => (
        // Its own click zone: the row opens the plan, this must not.
        <span onClick={(e) => e.stopPropagation()}>
          <Popconfirm title="Delete this plan?" description="Completed batches keep their figures." okText="Delete" okButtonProps={{ danger: true }} onConfirm={() => handleDelete(r.plan_id)}>
            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </span>
      ),
    },
  ];

  // The product's schedule. Only the start date is picked; the end date is
  // worked out from it, the batches and batches/day (the server stores the
  // same figure) and follows them as they change.
  const renderDates = (it, idx) => {
    const end = endDateFor(it.start_date, it.batches_per_day, it.batches);
    return (
      <>
        <div>
          {idx === 0 && <span style={S.lbl}>Start date</span>}
          <DatePicker size="small" format="DD MMM YYYY" placeholder="Start" style={{ width: 130 }}
            value={it.start_date ? dayjs(it.start_date) : null}
            onChange={(d) => patchItem(it.key, { start_date: d ? d.format('YYYY-MM-DD') : null })} />
        </div>
        <div>
          {idx === 0 && <span style={S.lbl}>End date</span>}
          <Tooltip title={end ? `Worked out: ${it.batches} batch${it.batches === 1 ? '' : 'es'} at ${it.batches_per_day} a day = ${daysFor(it.batches, it.batches_per_day)} day${daysFor(it.batches, it.batches_per_day) === 1 ? '' : 's'} from the start date${Number.isInteger(num(it.batches_per_day)) ? '' : ' (a half batch carries over to the next day)'}` : 'Worked out from the start date, batches and batches per day'}>
            <div className="mm-end-date" style={{ width: 130, height: 24, boxSizing: 'border-box', border: `1px solid ${C.line}`, borderRadius: 6, backgroundColor: '#f8fafc', padding: '0 8px', display: 'flex', alignItems: 'center', fontSize: 12.5, color: end ? C.ink : C.mute, cursor: 'help' }}>
              {end ? dayjs(end).format('DD MMM YYYY') : 'Auto'}
            </div>
          </Tooltip>
        </div>
      </>
    );
  };
  const renderPerDay = (it, idx) => (
    <div>
      {idx === 0 && <span style={S.lbl}>Batches / day</span>}
      {/* In halves: 1.5 = 3 batches every 2 days. Typing is left alone ("0" on
          the way to "0.5"); leaving the box snaps it to the nearest half. No
          step={0.5}: it would show whole numbers as "3.0". */}
      <InputNumber size="small" min={0.5} max={999} placeholder="—" value={it.batches_per_day}
        onChange={(v) => patchItem(it.key, { batches_per_day: v === null || v === undefined || v === '' ? null : v })}
        onBlur={(e) => {
          const t = String(e.target.value || '').trim();
          patchItem(it.key, { batches_per_day: t === '' ? null : Math.max(0.5, Math.round(num(t) * 2) / 2) });
        }}
        style={{ width: 78 }} />
    </div>
  );

  // Plan-level controls in their own row above the product lines, so every
  // product line has the same width and the batch boxes line up.
  const noEditChanges = !!editing && !editChanges.length;
  const renderToolbar = () => (
    <div className="mm-actions" style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 10, paddingBottom: 10, borderBottom: `1px dashed ${C.line}` }}>
      <div className="mm-plan-name">
        <span style={S.lbl}>Plan name (optional)</span>
        <Input size="small" placeholder="e.g. October campaign" value={planName} onChange={(e) => setPlanName(e.target.value)} style={{ width: 220 }} />
      </div>
      {editing && (
        <div style={{ flex: '1 1 260px', minWidth: 0, alignSelf: 'center', fontSize: 12.5 }}>
          {editChanges.length ? (
            <Popover placement="bottomLeft" title={`${editChanges.length} unsaved change${editChanges.length === 1 ? '' : 's'}`} content={
              <div style={{ fontSize: 12, lineHeight: 1.7, maxWidth: 420 }}>
                {editChanges.map((c, i) => <div key={i} style={{ color: c.kind === 'remove' || c.kind === 'down' ? C.red : c.kind === 'name' || c.kind === 'materials' || c.kind === 'schedule' ? C.ink : C.green }}>{c.text}</div>)}
              </div>
            }>
              <span style={{ color: C.amber, fontWeight: 700, cursor: 'help', borderBottom: `1px dotted ${C.amber}` }}>
                {editChanges.length} unsaved change{editChanges.length === 1 ? '' : 's'}
              </span>
              <span style={{ color: C.sub }}> · {editChanges.slice(0, 2).map((c) => c.text).join(' · ')}{editChanges.length > 2 ? ' …' : ''}</span>
            </Popover>
          ) : <span style={{ color: C.mute }}>No changes yet — change batches, dates, tick materials, add or remove a product, or rename.</span>}
        </div>
      )}
      <div style={{ display: 'flex', gap: 6, marginLeft: 'auto', flexShrink: 0 }}>
        <Tooltip title="Refresh the numbers"><Button size="small" icon={<ReloadOutlined />} onClick={loadRequirements} loading={reqLoading} /></Tooltip>
        <Button size="small" icon={<DownloadOutlined />} onClick={() => exportMaterials(combined, 'material-requirement')} disabled={!combined.length}>Export</Button>
        <Button size="small" type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving} disabled={!validItems.length || noEditChanges}>
          {editing ? 'Save Changes' : 'Save Plan'}
        </Button>
      </div>
    </div>
  );

  return (
    <div style={S.page} className="mm-tight">
      {/* header + planner in ONE card */}
      <div style={{ ...S.card, marginBottom: 10 }}>
        <div style={{ ...S.pad, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', borderBottom: view === 'new' ? `1px solid ${C.line}` : 'none' }}>
          <div>
            <h1 style={S.h1}>Material Management</h1>
            <div style={S.sub}>What your batches need, what is in stock, what to buy · live from production, read-only</div>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <Button size="small" type={view === 'new' && !editing ? 'primary' : 'default'} icon={<PlusOutlined />} onClick={startNewPlan}>New Plan</Button>
            <Button size="small" type={view !== 'new' ? 'primary' : 'default'} onClick={goToSavedPlans}>Saved Plans</Button>
          </div>
        </div>

        {view === 'new' && editing && (
          <div style={{ ...S.pad, paddingBottom: 0 }}>
            <Alert type="info" showIcon style={{ fontSize: 12, padding: '5px 10px' }}
              message={<span style={{ fontSize: 12 }}>
                Editing <b>{editing.plan_name || 'Untitled plan'}</b> <span style={{ ...S.mono, color: C.mute }}>({editing.plan_id})</span>.
                {' '}The plan keeps its place in the queue. Batches already dispensed or in progress cannot be removed.
              </span>}
              action={<Button size="small" onClick={cancelEdit}>Cancel</Button>} />
          </div>
        )}

        {view === 'new' && (
          <div style={S.pad}>
            {renderToolbar()}
            {items.map((it, idx) => {
              const sel = it.product_id ? productById(it.product_id) : null;
              // An existing product of an edited plan: its document is fixed
              // (remove it and add another instead), and its started batches
              // (dispensed + in progress) set the floor for the count.
              if (it.locked) {
                return (
                  <div key={it.key} className="mm-row" style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 7, flexWrap: 'wrap' }}>
                    <div className="mm-col-doc" style={{ flex: '1 1 478px', maxWidth: 628, minWidth: 220 }}>
                      {idx === 0 && <span style={S.lbl}>Product / BMR-BPR document</span>}
                      <div style={{ border: `1px solid ${C.line}`, borderRadius: 6, padding: '1px 8px', minHeight: 24, display: 'flex', alignItems: 'center', gap: 6, backgroundColor: '#f8fafc', fontSize: 12 }}>
                        {it.product_code && <span style={{ ...S.mono, fontWeight: 700, color: C.ink }}>{it.product_code}</span>}
                        <span style={{ color: C.sub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.product_name}</span>
                        {(it.completed > 0 || it.in_progress > 0) && (
                          <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 4 }}>
                            {it.completed > 0 && <Tag color="green" style={{ marginInlineEnd: 0, fontSize: 11.5 }}>{it.completed} dispensed</Tag>}
                            {it.in_progress > 0 && <Tag color="gold" style={{ marginInlineEnd: 0, fontSize: 11.5 }}>{it.in_progress} in progress</Tag>}
                          </span>
                        )}
                      </div>
                    </div>
                    {renderDates(it, idx)}
                    <div>
                      {idx === 0 && <span style={S.lbl}>Batches</span>}
                      <InputNumber size="small" min={Math.max(1, it.started || 0)} max={999} value={it.batches}
                        onChange={(v) => patchItem(it.key, { batches: Math.max(v || 1, it.started || 0, 1) })} style={{ width: 78 }} />
                    </div>
                    {renderPerDay(it, idx)}
                    <Tooltip title={it.started > 0 ? 'Cannot remove — some batches are already dispensed or in progress' : 'Remove this product'}>
                      <Button size="small" danger type="text" icon={<DeleteOutlined />} disabled={items.length === 1 || it.started > 0} onClick={() => removeItem(it.key)} />
                    </Tooltip>
                    {idx === items.length - 1 && (
                      <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={addItem}>Add product</Button>
                    )}

                  </div>
                );
              }
              return (
              <div key={it.key} className="mm-row" style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 7, flexWrap: 'wrap' }}>
                <div className="mm-col-fam" style={{ flex: '0 0 150px' }}>
                  {idx === 0 && <span style={S.lbl}>Product — {familyOptions.length} with a BOM</span>}
                  <Select showSearch size="small" placeholder={productsLoading ? 'Loading…' : 'e.g. ATS'}
                    value={it.family}
                    onChange={(v) => patchItem(it.key, { family: v, product_id: null, bom_line_ids: null })}
                    loading={productsLoading} options={familyOptions} filterOption={filterProduct}
                    style={{ width: '100%' }} />
                </div>
                <div className="mm-col-doc" style={{ flex: '1 1 320px', maxWidth: 470, minWidth: 220 }}>
                  {idx === 0 && <span style={S.lbl}>BMR / BPR document</span>}
                  <div style={{ display: 'flex', gap: 4 }}>
                    <Select showSearch size="small"
                      placeholder={it.family ? 'Select a document' : 'Pick a product first'}
                      disabled={!it.family}
                      value={it.product_id} onChange={(v) => patchItem(it.key, { product_id: v, bom_line_ids: null })}
                      options={docOptionsFor(it.family, new Set(items.filter((o) => o.key !== it.key && o.product_id).map((o) => o.product_id)))}
                      filterOption={filterProduct}
                      optionRender={({ data: o }) => (
                        <div className="mm-doc-opt">
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                            <span style={{ ...S.mono, fontSize: 12, fontWeight: 700, color: o.disabled ? C.mute : C.ink }}>{o.code || o.name}</span>
                            {o.type && <span className="mm-doc-tag">{o.type}</span>}
                            <span className="mm-doc-tag" style={o.disabled ? { color: C.red, borderColor: '#fecaca' } : undefined}>{o.meta}</span>
                          </div>
                          {o.code && o.name && <div style={{ fontSize: 12.5, color: C.sub, marginTop: 2 }}>{o.name}</div>}
                        </div>
                      )}
                      style={{ flex: 1, minWidth: 0 }} />
                    {/* Identifiers behind a button: the name alone is ambiguous
                        (many products share one) and the id is what you need to
                        query the database directly, but they clutter the row. */}
                    {sel && (
                      <Popover trigger="click" placement="bottomLeft" title="Document details" content={
                        <div style={{ fontSize: 12, lineHeight: 1.7, maxWidth: 360 }}>
                          <div><span style={{ color: C.mute }}>Product ID:</span> <b style={S.mono}>{sel.id}</b></div>
                          {sel.product_family && <div><span style={{ color: C.mute }}>Family:</span> {sel.product_family}</div>}
                          <div><span style={{ color: C.mute }}>Name:</span> {sel.name}</div>
                          {sel.bmr_bpr_code && sel.bmr_bpr_code !== 'N/A' && <div><span style={{ color: C.mute }}>Document:</span> <span style={S.mono}>{sel.bmr_bpr_code}</span></div>}
                          {sel.bmr_bpr_type && <div><span style={{ color: C.mute }}>Type:</span> {sel.bmr_bpr_type}</div>}
                          <div><span style={{ color: C.mute }}>Materials:</span> {sel.bom_material_count}</div>
                          {sel.theoretical_batch_size && <div><span style={{ color: C.mute }}>Batch size:</span> {sel.theoretical_batch_size}</div>}
                        </div>
                      }>
                        <Button size="small" icon={<InfoCircleOutlined />} />
                      </Popover>
                    )}
                  </div>
                </div>
                {renderDates(it, idx)}
                <div>
                  {idx === 0 && <span style={S.lbl}>Batches</span>}
                  <InputNumber size="small" min={1} max={999} value={it.batches} onChange={(v) => patchItem(it.key, { batches: v || 1 })} style={{ width: 78 }} />
                </div>
                {renderPerDay(it, idx)}
                <Button size="small" danger type="text" icon={<DeleteOutlined />} disabled={items.length === 1} onClick={() => removeItem(it.key)} />
                {idx === items.length - 1 && (
                  <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={addItem}>Add product</Button>
                )}

              </div>
              );
            })}
            <style>{`
              .mm-doc-opt, .mm-doc-opt * { white-space: normal; line-height: 1.35; }
              .mm-doc-opt { padding: 2px 0; }
              .mm-doc-tag { font-size: 10.5px; color: ${C.sub}; border: 1px solid ${C.line}; border-radius: 4px; padding: 0 5px; background: #f8fafc; }
              @media (max-width: 640px) {
                .mm-row { align-items: stretch !important; padding-bottom: 8px; border-bottom: 1px dashed ${C.line}; }
                .mm-col-fam, .mm-col-doc { flex: 1 1 100% !important; max-width: none !important; min-width: 0 !important; }
                .mm-actions { margin-left: 0 !important; width: 100%; flex-wrap: wrap; }
                .mm-plan-name { flex: 1 1 100%; }
                .mm-plan-name .ant-input { width: 100% !important; }
              }
            `}</style>
          </div>
        )}
      </div>

      {/* ---------------- NEW PLAN ---------------- */}
      {view === 'new' && (
        !pickedItems.length ? (
          <div style={{ ...S.card, padding: 28 }}>
            <Empty description={<span style={{ fontSize: 12.5 }}>Pick a product above to see what material it needs</span>} image={Empty.PRESENTED_IMAGE_SIMPLE} />
          </div>
        ) : (
          <>
            {validItems.length > 0 && (
              <Spin spinning={reqLoading}>
                <StatusStrip materials={combined} batches={totalBatches} products={validItems.length}
                  batchesPossible={batchesPossible}
                  batchSize={validItems.length === 1 ? (productById(validItems[0].product_id)?.theoretical_batch_size || '—') : null} />
                <ShortList materials={combined} />
              </Spin>
            )}
            <div style={{ ...S.card, ...S.pad }}>
              <Tabs size="small" items={[
                {
                  // The plan's own BOM. Not under a spinner, so ticking goes on
                  // while the numbers recalculate.
                  key: 'bom',
                  label: (
                    <span>
                      Select materials
                      {untickedItems.length > 0 && <WarningFilled style={{ color: C.red, fontSize: 12, marginLeft: 5 }} />}
                    </span>
                  ),
                  children: <>
                    <div style={{ ...S.hint, marginBottom: 8 }}>
                      Tick the BOM lines this plan needs — a whole dispensing sequence, or single materials (for example one vendor&apos;s code out of its alternates).
                      {' '}Everything else in the plan uses only the ticked lines: required, stock, short, and when a batch counts as complete — once every ticked material has been issued to it (for ticked alternates, any one of them).
                    </div>
                    <Tabs size="small" type="card" items={pickedItems.map((it) => {
                      const p = productById(it.product_id) || { name: it.product_name };
                      const b = boms[it.product_id] || { loading: true, lines: [] };
                      const n = tickedCount(b.lines, it.bom_line_ids);
                      return {
                        key: String(it.product_id),
                        label: (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ maxWidth: 190, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p?.name || 'Product'}</span>
                            {!b.loading && !b.error && <span style={{ ...S.mono, fontSize: 12, color: !n ? C.red : n < b.lines.length ? C.amber : C.mute }}>{n}/{b.lines.length}</span>}
                          </span>
                        ),
                        children: <BomPicker lines={b.lines} value={it.bom_line_ids} loading={b.loading} error={b.error}
                          onChange={(v) => patchItem(it.key, { bom_line_ids: v })} />,
                      };
                    })} />
                  </>,
                },
                {
                  key: 'c', label: `Combined (${combined.length})`,
                  children: <>
                    <div style={{ ...S.hint, marginBottom: 8 }}>Required is added across products; stock counted once per material — it is the same physical stock. Stock already reserved by saved plans is taken off first.</div>
                    <MaterialTable rows={combined} loading={reqLoading} emptyText="No BOM materials for the selected products" />
                  </>,
                },
                ...validItems.map((it) => {
                  // An edited plan's product may no longer be in the approved
                  // dropdown list, so fall back to what the plan stored.
                  const p = productById(it.product_id) || { code: it.product_code, name: it.product_name, theoretical_batch_size: results[it.product_id]?.product?.theoretical_batch_size };
                  const done = it.completed || 0;
                  const inProg = it.in_progress || 0;
                  return {
                    key: String(it.product_id),
                    label: <ProductTabLabel name={p?.name} batches={it.batches} materials={results[it.product_id]?.materials} />,
                    children: <>
                      <div style={{ ...S.hint, marginBottom: 8 }}>
                        <b style={{ color: C.ink }}>{p?.name}</b> · {it.batches} batch{it.batches === 1 ? '' : 'es'}
                        {done > 0 && <> ({done} dispensed, <b>{it.batches - done}</b> still to run — only those need material)</>}
                        {inProg > 0 && <> · {inProg} in progress need only what they have not been issued yet</>}
                        {Array.isArray(it.bom_line_ids) && <> · <b>{it.bom_line_ids.length}</b> BOM line{it.bom_line_ids.length === 1 ? '' : 's'} ticked</>}
                        {' · '}theoretical batch size {p?.theoretical_batch_size || '—'}
                      </div>
                      <MaterialTable rows={results[it.product_id]?.materials} loading={reqLoading} emptyText="This product has no BOM configured"
                        batches={{ planned: it.batches, ...(done > 0 ? { dispensed: done, left: it.batches - done } : {}) }} />
                    </>,
                  };
                }),
              ]} />
            </div>
          </>
        )
      )}

      {/* ---------------- PLAN LIST ---------------- */}
      {view === 'list' && (
        <div style={{ ...S.card, padding: 4 }}>
          <Table columns={planColumns} dataSource={plans.map((p) => ({ ...p, key: p.plan_id }))} loading={plansLoading}
            onRow={(r) => ({ onClick: () => openPlan(r.plan_id), style: { cursor: 'pointer' } })}
            size="small" pagination={plans.length > 20 ? { pageSize: 20, showSizeChanger: false, size: 'small' } : false}
            locale={{
              emptyText: <div style={{ padding: 24 }}>
                <Empty description={<span style={{ fontSize: 12.5 }}>No plans saved yet</span>} image={Empty.PRESENTED_IMAGE_SIMPLE}>
                  <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => setView('new')}>Create your first plan</Button>
                </Empty>
              </div>,
            }} />
        </div>
      )}

      {/* ---------------- PLAN DETAIL ---------------- */}
      {view === 'detail' && (
        <Spin spinning={detailLoading}>
          {!detail ? <div style={{ ...S.card, padding: 28 }}><Empty description="Plan not found" image={Empty.PRESENTED_IMAGE_SIMPLE} /></div> : (
            <>
              <div style={{ ...S.card, ...S.pad, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 220px' }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.ink }}>{detail.plan_name || 'Untitled plan'}</div>
                  <div style={{ ...S.mono, fontSize: 11.5, color: C.mute }}>{detail.plan_id}</div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <Button size="small" icon={<ArrowLeftOutlined />} onClick={() => setView('list')}>Back</Button>
                  <Button size="small" icon={<EditOutlined />} onClick={() => startEdit(detail)} disabled={productsLoading}>Edit</Button>
                  <Button size="small" icon={<DownloadOutlined />} onClick={() => handleDownload(detail.plan_id)} loading={downloading}>Download</Button>
                  <Tooltip title="Excel day plan: for each date, the products running and their number of batches (start date + batches per day) — green when the materials are in stock, red when short.">
                    <Button size="small" icon={<DownloadOutlined />} onClick={() => handleStatusDownload(detail.plan_id)} loading={statusDownloading}>Material status</Button>
                  </Tooltip>
                  <Button size="small" icon={<ReloadOutlined />} onClick={() => openPlan(detail.plan_id)} />
                </div>
              </div>

              <Warnings items={detail.warnings} />

              <div style={{ ...S.card, ...S.pad }}>
                <Tabs size="small" items={[
                  {
                    // Left = not started + in progress: both still need material.
                    key: 'live', label: `Requirement — ${(detail.summary?.running ?? 0) + (detail.summary?.in_progress ?? 0)} left`,
                    children: !(detail.products || []).length
                      ? <Empty description={<span style={{ fontSize: 12.5 }}>Every batch is complete — nothing left to calculate</span>} image={Empty.PRESENTED_IMAGE_SIMPLE} />
                      : <Tabs size="small" type="card" items={[
                        // Only worth a tab when there is more than one product to add up.
                        ...(detail.products.length > 1 ? [{
                          key: 'combined',
                          label: <ProductTabLabel name="Combined" batches={(detail.summary?.running ?? 0) + (detail.summary?.in_progress ?? 0)} done={detail.summary?.completed ?? 0} materials={detailCombined} />,
                          children: <>
                            <div style={{ ...S.hint, marginBottom: 8 }}>All running batches of this plan together. Required is added across products; stock is counted once per material — it is the same physical stock.</div>
                            <MaterialTable rows={detailCombined} emptyText="No BOM materials for this plan" />
                          </>,
                        }] : []),
                        ...detailProducts.map((p) => {
                        const pi = (detail.plan_items || []).find((x) => x.product_id === p.product_id) || {};
                        return {
                        key: String(p.product_id),
                        label: <ProductTabLabel name={p.product_name} batches={p.running} done={p.done} materials={p.live_materials} />,
                        children: <>
                          <div style={{ ...S.hint, marginBottom: 8 }}>
                            <b style={{ color: C.ink }}>{p.product_name}</b>
                            <span style={{ ...S.mono, color: C.mute }}> (product_id {p.product_id})</span>
                            {' · '}{p.running + p.done} planned · <span style={{ color: C.blue }}>{p.done} dispensed</span>
                            {pi.in_progress > 0 && <> · <span style={{ color: C.amber }}>{pi.in_progress} in progress</span></>}
                            {' · '}<b>{p.running}</b> left
                            {Array.isArray(pi.bom_line_ids) && <> · <b>{pi.bom_line_ids.length}</b> BOM line{pi.bom_line_ids.length === 1 ? '' : 's'} ticked</>}
                            {scheduleText(pi) && <> · <span style={{ color: C.ink }}>{scheduleText(pi)}</span></>}
                            {p.theoretical_batch_size ? ` · theoretical batch size ${p.theoretical_batch_size}` : ''} · recalculated live, after the stock reserved by batches ahead of it in the queue
                          </div>
                          <ShortList materials={p.live_materials} />
                          <MaterialTable rows={p.live_materials} emptyText="This product has no BOM configured"
                            batches={{ planned: p.running + p.done, dispensed: p.done, left: p.running }} />
                        </>,
                        };
                      })]} />,
                  },
                  {
                    key: 'progress', label: `In progress — ${detail.summary?.in_progress ?? 0}`,
                    children: !(detail.in_progress_batches || []).length
                      ? <Empty description={<span style={{ fontSize: 12.5 }}>No batch is part-way through dispensing.</span>} image={Empty.PRESENTED_IMAGE_SIMPLE} />
                      : <InProgressBatches batches={detail.in_progress_batches} />,
                  },
                  {
                    key: 'done', label: `Completed — ${detail.summary?.completed ?? 0}`,
                    children: !(detail.completed_batches || []).length
                      ? <Empty description={<span style={{ fontSize: 12.5 }}>No batches completed yet. A batch is frozen here once every ticked material has been issued to it.</span>} image={Empty.PRESENTED_IMAGE_SIMPLE} />
                      // Keyed by plan: opening another plan starts with an empty search.
                      : <CompletedBatches key={detail.plan_id} batches={detail.completed_batches} />,
                  },
                ]} />
              </div>
            </>
          )}
        </Spin>
      )}
    </div>
  );
};

export default MaterialManagement;
